import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent, type ReactElement, type ReactNode } from "react";
import type { BoardTableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal, useSignal, type Signal } from "../net/signal";
import { Overlay } from "../ui/Overlay";
import { ResultAd } from "../ui/Ads";
import { avatarSrc } from "./avatar";
import { fa } from "./cards";
import { botBare } from "./seats";
import { BotIcon } from "../ui/BotIcon";
import { BONK_DX, BoardOnline, menchOrdinal } from "../board/controller";
import { Domino } from "./DominoTable";
import { BoardChatButtons, RoundSeat, VoiceMarks, reactSrc, useSeatBubbles, useVoiceSeat, type SeatBubble } from "./RoundSeat";
import { ChatFab } from "../ui/Chat";
import { Die3D, DieCube } from "./Dice3D";
import { diceVars } from "./diceSkins";
import { FIELD15, SETTLE_WOBBLE, THROW_R, planRoll, qCss, rollAt, sampleAt, throwPath, throwStart, type ThrowField } from "../board/diceThrow";
import { reactionPhrases } from "./chat";
import {
  CarArt, IconArt, MAGIC_LOOK, MagicArt, MarpeleBoardArt, MarpeleDanger, MarpeleMagics, SnakeImage, biteOrigin, jumpPath, snakeBite, snakeHeads, themeOf,
  type Jumps,
} from "./MarpeleArt";
import { KNIGHT_POSES, POTION_AT, STRIKE_TIP } from "./marpeleKnightData";
import { ms } from "./speed";
import { diceSfx } from "./sfx";
import { preloadSfx } from "./sfx";
import {
  NARD_FIELD, NBARSTACK, NBW, NCHK, NDIA, NDIE, NH, NOW, NP, NPW, NSTACK, NTL, NTOP, NBOT, NW, NX0, NX1, NX2, NX3, NX4,
  nardBarXY, nardChkXY, nardCol, nardCorner, nardLandXY, nardLocAt, nardOffXY, nardSnap, nardThrowUnitPx, nardThrowXY,
} from "../board/nardGeom";
import {
  BASE, DICE_ONE, DICE_SUM, DOOZ_H, DOOZ_W, GOAL, MAIN_LAST, MENCH_COLORS, MENCH_HOME, POWER_DOUBLE, POWER_FA, POWER_SHIELD, POWER_SWAP,
  menchPowerXY, MENCH_LOOP, MENCH_NONE, MENCH_SAFE, MENCH_THEMES,
  NARD_BAR, NARD_IVORY, NARD_OFF,
  doozColor, marpeleXY, menchBadge, menchBadgeRank, menchColor, menchInPlay, menchPartner, menchPickable, menchProgress,
  menchTokenXY, menchWon, menchXY, nardColor, nardPips, seatColor, toServer, marpeleCorner, seatArt,
  type BoardLocal, type DoozLocal, type MarpeleLocal, type MarpelePower, type MenchBadge, type MenchHint, type MenchLocal, type NardLocal,
} from "../board/state";
import { CountryFlag } from "../ui/CountryFlag";

/**
 * میزِ تخته‌ای (منچ، دوز) — همتای MenchView و DoozView ِ اپ، ولی با SVG و بی صحنه‌ی امری.
 *
 * چیدمانِ عمودیِ همان BoardKit: حریف‌ها بالا، تخته وسط، ردیفِ خودت، و دکمه‌ی اصلی پایین
 * («تاس بریز»، یا قرصِ «نوبتِ فلانی»). رنگِ هر کس همان رنگِ سرور است، روی همه‌ی صفحه‌ها؛
 * تخته فقط می‌چرخد تا رنگِ تو پایین-چپ باشد.
 */
export function BoardTable({ state }: { state: BoardTableState }) {
  const sig = useRef<Signal<BoardOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<BoardOnline | null>(null);
  useEffect(() => {
    // کنترلر در اثر ساخته می‌شود و نه در رندر: StrictMode اثر را دو بار می‌زند و
    // کنترلرِ دورریخته صفِ مرده دارد.
    const c = new BoardOnline(state.game);
    sig.current!.value = c;
    c.start();
    preloadSfx();
    return () => { c.dispose(); sig.current!.value = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={`table bt bt-${state.game}`}>
      <Live sig={sig.current} />
    </div>
  );
}

function Live({ sig }: { sig: Signal<BoardOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <LiveInner c={c} />;
}

function LiveInner({ c }: { c: BoardOnline }) {
  const m = useSignal(c.model);
  const ended = useSignal(c.ended);
  if (!m) return <div className="bt-wait muted">در حال اتصال به میز…</div>;
  return (
    <>
      {m.game === "mench" ? <Mench m={m} c={c} />
        : m.game === "dooz" ? <Dooz m={m} c={c} />
        : m.game === "nard" ? <Nard m={m} c={c} />
        : m.game === "domino" ? <Domino m={m} c={c} />
        : <Marpele m={m} c={c} />}
      {ended && <GameOver m={m} c={c} />}
      {(m.game === "mench" || m.game === "nard") && <Confetti c={c} />}
    </>
  );
}

/**
 * تا `until` (performance.now) روشن است و سرِ همان لحظه یک بار دوباره می‌کشد — برای
 * چیزهای گذرای بی‌سیگنال‌پایان: نوارِ واکنش، حبابِ پلاک، خطِ وضعیتِ گذرا.
 */
function useActive(until: number): boolean {
  const [, force] = useState(0);
  const on = until > performance.now();
  useEffect(() => {
    if (until <= performance.now()) return;
    const t = setTimeout(() => force((x) => x + 1), until - performance.now() + 20);
    return () => clearTimeout(t);
  }, [until]);
  return on;
}

// ─────────────────────────── پلاک و ردیفِ پایین ───────────────────────────

function Plate({ m, seat, color, sub, badges, cls }:
  { m: BoardLocal; seat: number; color: string; sub: ReactNode; badges?: ReactNode; cls?: string }) {
  const s = m.seats[seat];
  const turn = !m.over && m.turn === seat;
  const me = seat === 0 && !m.watching;
  const name = me ? "تو" : botBare(s?.name || "حریف", s?.bot);
  const vox = useVoiceSeat(s?.playerId);
  return (
    <div className={`bt-plate${turn ? " turn" : ""}${cls ? ` ${cls}` : ""}${vox}`} style={{ "--tok": color } as CSSProperties}
         aria-label={`${name}${turn ? " — نوبت" : ""}`}>
      <span className="pav">
        {vox && <VoiceMarks />}
        <img src={avatarSrc(s?.avatar)} alt="" width={40} height={40} />
        <CountryFlag code={s?.country} />
      </span>
      <div className="t">
        <b>{s?.bot && !me && <BotIcon size={13} />}{name}</b>
        <span>{sub}{badges}</span>
      </div>
      {turn && <i className="tag">نوبت</i>}
    </div>
  );
}

function Timer({ c }: { c: BoardOnline }) {
  const t = useSignal(c.timer);
  if (!t) return null;
  const f = Math.max(0, Math.min(1, t.left / Math.max(0.1, t.total)));
  return (
    <div className={`bt-timer${f < 0.25 ? " low" : ""}`} style={{ "--f": f } as CSSProperties} aria-label="مهلت نوبت">
      <b>{fa(Math.ceil(t.left))}</b>
    </div>
  );
}

function Status({ text }: { text: string }) {
  return <div className="bt-status" role="status">{text}</div>;
}

/**
 * میانه‌ی میز: ردیفِ حریف‌ها، تخته و ردیفِ خودت، کنارِ هم و وسطِ صفحه — همان
 * BoardKit.Layout ِ اپ. تخته تا جایی بزرگ می‌شود که با دو ردیف در بلندیِ صفحه جا
 * شود (`rows` = بلندیِ آن دو ردیف با فاصله‌ها). پیش‌تر روی صفحه‌ی کوتاه تخته از
 * قاب بلندتر می‌شد و ردیفِ تاس و دکمه‌ی «تاس بریز» بیرون می‌افتادند.
 */
function Mid({ ar, rows, top, me, children }: { ar: number; rows: number; top: ReactNode; me: ReactNode; children: ReactNode }) {
  return (
    <div className="bt-mid" style={{ "--ar": ar, "--rows": `${rows}px` } as CSSProperties}>
      {top}
      <div className="bt-stage">{children}</div>
      {me}
    </div>
  );
}

// ─────────────────────────── منچ ───────────────────────────

/**
 * میزِ منچ. همه‌چیز از نمای سرور است (قانونی این‌جا نیست): تم، قانون‌ها، شانس‌ها، رتبه‌ها،
 * صاحبِ نوبت و راهنمای حرکت‌ها از دُمِ نسخه‌ی ۲ ِ extra؛ بی دُم، همان منچِ کلاسیکِ پیشین.
 */
function Mench({ m, c }: { m: MenchLocal; c: BoardOnline }) {
  const rolling = useSignal(c.rolling);
  const note = useSignal(c.mnNote);
  const noteOn = useActive(note?.until ?? 0);
  const bubble = useSignal(c.mnBubble);
  const bubbleOn = useActive(bubble?.until ?? 0);
  const reactOn = useActive(useSignal(c.reactUntil));
  const [stay, setStay] = useState(false);
  const home = (s: number) => m.pos[s]?.filter((p) => p === GOAL).length ?? 0;
  const inBase = (s: number) => m.pos[s]?.filter((p) => p === BASE).length ?? 0;
  // مخرجِ «از چند رسید» = مهره‌های در بازی («سریع»: دو)
  // «· n در خانه» جدا است تا روی پلاکِ باریک (کنارِ تاس روی گوشی) پنهان شود و «n از m رسید»
  // کامل بماند — خانه‌نشین‌ها روی خودِ تخته پیدا هستند (.mn-inbase در style.css)
  // زیرِ چهره‌ی گوشه کوتاه: «n از m رسید» — مهره‌های خانه روی خودِ تخته پیدا هستند (همان اپ)
  const sub = (s: number): ReactNode => {
    const r = m.rank[s] ?? 0;
    if (r > 0 && !m.over && m.opt.places) return `نفر ${menchOrdinal(r)}، تمام کرد`;
    return `${fa(home(s))} از ${fa(menchInPlay(m, s))} رسید`;
  };
  void inBase;
  const col = (s: number) => menchColor(m.corner[s] ?? 0, m.shift);
  const teams = m.opt.teams && m.n === 4;
  // تیمی: تیمِ هر صندلی از صندلیِ سرورش — ۰ و ۲ تیمِ ۱، ۱ و ۳ تیمِ ۲ (همان RoomV3 و اتاقِ وب)
  const team = (s: number) => toServer(s, m.me, m.n) % 2;
  // نشانِ روی چهره: تیم، و نیروی دستِ صندلی («نیروها»: سپر، دوبرابر)
  const badges = (s: number) => (
    <>
      {teams && <i className={`mn-team t${team(s)}`}>{!m.watching && s === menchPartner(0) ? "یار تو" : `تیم ${fa(team(s) + 1)}`}</i>}
      {(m.shield[s] || m.dbl[s]) && (
        <i className="mn-held" role="img" aria-label={[m.shield[s] ? POWER_FA[POWER_SHIELD] : "", m.dbl[s] ? POWER_FA[POWER_DOUBLE] : ""].filter((x) => x).join(" و ")}>
          {m.shield[s] && <PowerGlyph kind={POWER_SHIELD} size={20} />}
          {m.dbl[s] && <PowerGlyph kind={POWER_DOUBLE} size={20} />}
        </i>
      )}
    </>
  );
  const chat = useSeatBubbles(m.n, m.me);
  const thrown = useSignal(c.mnThrow);
  const timer = useSignal(c.timer);
  const mine = !m.watching && (m.canRoll || m.tokens.length > 0);
  const secs = timer && mine ? Math.max(0, Math.ceil(timer.left)) : 0;
  const frac = timer && mine ? Math.max(0, Math.min(1, timer.left / Math.max(0.1, timer.total))) : 1;
  // حبابِ صندلی: پیامِ چتِ خودش، یا حبابِ لحظه‌ی بازی (😱 ِ زده‌شده — نشانه‌ی «شوکه»)
  const bubbleOf = (s: number): SeatBubble | undefined => {
    if (bubbleOn && bubble?.seat === s)
      return { key: `b${bubble.id}`, text: bubble.text, art: bubble.text === "😱" ? reactSrc("r_shock") : undefined };
    return chat.get(s);
  };
  const seatOf = (s: number) => {
    const ss = m.seats[s];
    const me = s === 0 && !m.watching;
    return (
      <RoundSeat name={me ? "تو" : botBare(ss?.name || "حریف", ss?.bot)} avatar={ss?.avatar} country={ss?.country} bot={ss?.bot} me={me} color={col(s)}
                 turn={!m.over && m.turn === s} secs={s === 0 ? secs : 0} frac={frac} sub={sub(s)} extra={badges(s)}
                 cls={`mn-seat${teams ? ` mn-tm${team(s)}` : ""}`} bubble={bubbleOf(s)} pid={ss?.playerId} />
    );
  };
  // هر گوشه: چهره و جعبه‌ی تاسش رو به میانه؛ گوشه‌ی بی‌بازیکن خالی
  const corner = (k: number) => {
    const s = m.corner.findIndex((x) => x === k);
    if (s < 0) return <div className={`mn3-seat c${k} empty`} />;
    return (
      <div className={`mn3-seat c${k}`}>
        {seatOf(s)}
        <DiceBox m={m} s={s} rolling={rolling && m.turn === s} onRoll={(v) => c.roll(v)} forPartner={s === 0 && myTurnForPartnerOf(m)}
                 throwing={thrown?.seat === s} />
      </div>
    );
  };
  // تیمی و مهره‌های صاحبِ نوبت رسیده: برای یارش می‌ریزد (صاحب ≠ نوبت)
  const forPartner = teams && m.owner !== m.turn;
  const myTurnForPartner = forPartner && m.turn === 0 && !m.watching;
  const status = m.canRoll ? (myTurnForPartner ? "نوبت توست؛ برای یارت روی تاس بزن" : "نوبت توست؛ روی تاس بزن")
    : m.tokens.length > 0 ? (myTurnForPartner ? "برای یارت بازی می‌کنی — یکی از مهره‌های روشن را لمس کن" : "یکی از مهره‌های روشن را لمس کن")
    : m.over ? ""
    : noteOn && note ? note.text
    : `نوبت ${c.seatName(m.turn)}${forPartner ? (m.turn === 0 && !m.watching ? " — برای یارت" : " — برای یارش") : ""}`;
  const done = !m.watching && !m.over && m.opt.places && (m.rank[0] ?? 0) > 0;
  return (
    <>
      <Mid ar={1} rows={252}
        top={<div className="mn3-row top">{corner(1)}{m.opt.timed > 0 && !m.over && <MatchClock m={m} />}{corner(2)}</div>}
        me={<div className="mn3-row bottom">{corner(0)}<BoardChatButtons onReact={(k) => c.react(k)} />{corner(3)}</div>}>
        <MenchBoard m={m} c={c} />
      </Mid>
      <div className="bt-act">
        {done && !stay ? (
            // «تا نفرِ آخر»: کارِ خودم تمام شد — بمانم و تماشا کنم یا بروم (جایم محفوظ است)
            <div className="mn-done" role="group" aria-label="تمام کردی">
              <span>نفر {menchOrdinal(m.rank[0]!)} شدی</span>
              <button className="btn ghost" onClick={() => setStay(true)}>تماشای بقیه</button>
              <button className="btn" onClick={() => client.exitTable()}>خروج</button>
            </div>
          )
          : reactOn && !m.watching && m.tokens.length === 0 && !m.canRoll ? <Reactions c={c} cls="mp-react mn-react" />
          : <Status text={status} />}
      </div>
    </>
  );
}

/**
 * «زمان‌دار»: ثانیه‌شمارِ مسابقه از زمانِ مانده‌ی آخرین نما. هر نما از نو هم‌گامش می‌کند؛ دقیقه‌ی
 * آخر قرمز و تپنده.
 */
function MatchClock({ m }: { m: MenchLocal }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, m.leftMs - (performance.now() - m.leftAt));
  const sec = Math.ceil(left / 1000);
  const text = `${fa(Math.floor(sec / 60))}:${fa(sec % 60).padStart(2, "۰")}`;
  return (
    <div className={`mn-clock${sec <= 60 ? " low" : ""}`} role="timer" aria-label={`زمان مانده ${text}`}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l3 2M9 2h6" /></svg>
      <b>{m.leftMs > 0 ? text : "—"}</b>
    </div>
  );
}

/** نشانِ یک نیرو: سپر (آبی)، دوبرابر (طلایی)، جابه‌جایی (بنفش) — روی خانه‌ی درخشان و کنارِ چهره. */
function PowerGlyph({ kind, size }: { kind: number; size?: number }) {
  const g = (
    <>
      <circle r={0.3} className={`mn-pw-disc k${kind}`} />
      {kind === POWER_SHIELD && <path d="M0,-0.19 L0.15,-0.12 L0.13,0.04 Q0.09,0.15 0,0.2 Q-0.09,0.15 -0.13,0.04 L-0.15,-0.12 Z" fill="#fff" />}
      {kind === POWER_DOUBLE && <text y={0.085} textAnchor="middle" fontSize={0.24} fontWeight={800} fill="#3a2600">×۲</text>}
      {kind === POWER_SWAP && (
        <path d="M-0.15,-0.06 H0.13 M0.07,-0.12 L0.14,-0.06 L0.07,0 M0.15,0.07 H-0.13 M-0.07,0.01 L-0.14,0.07 L-0.07,0.13"
              fill="none" stroke="#fff" strokeWidth={0.045} strokeLinecap="round" strokeLinejoin="round" />
      )}
    </>
  );
  if (size === undefined) return g;
  return <svg viewBox="-0.32 -0.32 0.64 0.64" width={size} height={size} aria-hidden="true">{g}</svg>;
}

/** قفلِ «شکارچی» سرِ راهروِ پایانی — تا صاحبش مهره‌ای نزده. */
function GateLock({ x, y }: { x: number; y: number }) {
  return (
    <g className="mn-lock" transform={`translate(${x} ${y})`} pointerEvents="none">
      <circle r={0.32} fill="#14122be0" />
      <path d="M-0.09,-0.02 V-0.1 A0.09,0.09 0 0 1 0.09,-0.1 V-0.02" fill="none" stroke="#ffd84a" strokeWidth={0.05} />
      <rect x={-0.14} y={-0.03} width={0.28} height={0.2} rx={0.04} fill="#ffd84a" />
      <circle cy={0.06} r={0.03} fill="#14122b" />
    </g>
  );
}

/** تیمی و مهره‌های خودم همه رسیده: این تاس مهره‌های یارم را می‌برد. */
const myTurnForPartnerOf = (m: MenchLocal) => m.opt.teams && m.n === 4 && m.owner !== m.turn && m.turn === 0 && !m.watching;

/**
 * جعبه‌ی تاسِ یک صندلی کنارِ چهره‌اش: تاسِ نوبت در جعبه‌ی صاحبِ نوبت است. جعبه‌ی خودت دکمه‌ی ریختن است —
 * دکمه‌ی جدا نیست ([[tap-the-object]])؛ وقتی نوبتِ توست می‌درخشد و «بریز» رویش می‌آید. کشیدنِ انگشت روی آن
 * تاس را به همان سو روی تخته پرتاب می‌کند (ThrownDie)؛ در طولِ پرتاب جعبه خالی است.
 */
/**
 * کشیدنِ انگشت روی تاسِ خودت = پرتاب به همان سو؛ لمسِ خالی = رو به وسط. سرعت به «خانه‌ی منچ» (۱/۱۵ ِ تخته) بر ثانیه —
 * unitPx پیکسلِ همان واحد روی تخته‌ی همین بازی است (منچ و مارپله، ThrownDie هر دو را به همان واحد می‌کشد).
 */
function useSwipeRoll(can: boolean, onRoll: (swipe?: [number, number]) => void, unitPx: () => number) {
  const down = useRef<{ x: number; y: number; t: number } | null>(null);
  const release = (e: RPointerEvent<HTMLButtonElement>) => {
    const d = down.current;
    down.current = null;
    if (!d || !can) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y, dt = Math.max(16, performance.now() - d.t);
    const cell = unitPx() || 40;
    onRoll(Math.hypot(dx, dy) > 14 ? [(dx / dt) * 1000 / cell, (dy / dt) * 1000 / cell] : undefined);
  };
  return {
    onPointerDown: can ? (e: RPointerEvent<HTMLButtonElement>) => { down.current = { x: e.clientX, y: e.clientY, t: performance.now() }; e.currentTarget.setPointerCapture?.(e.pointerId); } : undefined,
    onPointerUp: can ? release : undefined,
    onPointerCancel: () => { down.current = null; },
    // صفحه‌کلید (Enter/فاصله) کلیکِ بی‌اشاره می‌دهد؛ کلیکِ موشی بعد از pointerup است و پرتاب همان‌جا شد
    onClick: can ? (e: { detail: number }) => { if (e.detail === 0) onRoll(); } : undefined,
  };
}

/** پیکسلِ یک واحدِ پرتاب (۱/۱۵ ِ تخته‌ی منچ بی قاب) روی تخته‌ی منچ. */
const menchUnitPx = () => (document.querySelector(".mn-board")?.getBoundingClientRect().width ?? 0) / 16.2;

function DiceBox({ m, s, rolling, onRoll, forPartner, throwing }:
  { m: MenchLocal; s: number; rolling: boolean; onRoll: (swipe?: [number, number]) => void; forPartner: boolean; throwing: boolean }) {
  const turn = !m.over && m.turn === s;
  const can = s === 0 && !m.watching && m.canRoll;
  const swipe = useSwipeRoll(can, onRoll, menchUnitPx);
  return (
    <button className={`mn-dbox${turn ? " turn" : ""}${can ? " on" : ""}`} disabled={!can} {...swipe}
            aria-label={can ? `تاس بریز${forPartner ? " · برای یارت" : ""}` : turn ? `تاس نوبت: ${fa(m.die)}` : "تاس"}>
      {turn && !throwing && <Die3D value={m.die > 0 ? m.die : 6} rolling={rolling} size="calc(var(--mn-av, 64px) * .44)" faded={m.die === 0} from={[1, -1]}
                                   skin={m.seats[s]?.dice} />}
      {can && <span className="tag">بریز</span>}
      {turn && m.mult === 2 && <span className="mn-x2" aria-label="دوبرابر">×۲</span>}
      {s === 0 && m.tries > 1 && m.phase === 0 && !m.over && (
        <span className="mn-tries" role="img" aria-label={`${fa(m.tries)} شانس`}>
          {Array.from({ length: Math.min(3, m.tries) }, (_, i) => <i key={i} />)}
        </span>
      )}
    </button>
  );
}

/** پنج‌پر برای خانه‌ی امن؛ r = شعاعِ بیرونی (واحدِ خانه). */
function starPoints(x: number, y: number, r: number, n = 5, inner = 0.45): string {
  const pts: string[] = [];
  for (let i = 0; i < 2 * n; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / n, rr = i % 2 === 0 ? r : r * inner;
    pts.push(`${(x + Math.cos(a) * rr).toFixed(3)},${(y + Math.sin(a) * rr).toFixed(3)}`);
  }
  return pts.join(" ");
}

/** برچسبِ نشانِ هر شبح برای صفحه‌خوان. */
const BADGE_FA: Record<Exclude<MenchBadge, "">, string> = {
  capture: "مهره‌ی حریف را می‌زند", goal: "به مقصد می‌رسد", danger: "خطر: حریف می‌تواند بزندش", pair: "دیوار می‌سازد", safe: "خانه‌ی امن",
};

/**
 * آدمکِ منچ (tools/mench_piece_cut.py): ۱۸۰×۲۴۰، کفِ پا ۱۰px بالای لبه‌ی پایین. بلندی ۱٫۷ خانه و کفِ پا
 * ۰٫۳ خانه پایینِ مرکزِ خانه — همان MenchArt.Piece ِ اپ. رنگِ آرت: ۰ قرمز، ۱ سبز، ۲ زرد، ۳ آبی (MENCH_COLORS).
 */
const PIECE_H = 1.7, PIECE_FEET = 0.3, FEET_IN_ART = 10 / 240;
type MenchPose = "idle" | "scared" | "step1" | "step2" | "jump" | "fallen" | "cheer";
const pieceIdx = (corner: number, shift: number) => (((corner + shift) % 4) + 4) % 4;
/** girl: آدمکِ دختر (g*) به جای پسر (p*) — انتخابِ صاحبِ صندلی (BoardSeat.piece). */
const pieceSrc = (ci: number, pose: MenchPose, girl = false) => `/art/mench/${girl ? "g" : "p"}${ci}-${pose}.webp`;

/** تصویرِ آدمک با کفِ پا روی (۰، ۰). */
function PieceArt({ ci, pose, girl = false, h = PIECE_H }: { ci: number; pose: MenchPose; girl?: boolean; h?: number }) {
  const w = h * 0.75;
  return <image href={pieceSrc(ci, pose, girl)} x={-w / 2} y={-h * (1 - FEET_IN_ART)} width={w} height={h} preserveAspectRatio="none" />;
}

/** پیکانِ سفیدِ خانه‌ی شروع (بی «خانه‌ی امن») رو به راه. */
function StartMark({ x, y, dx, dy }: { x: number; y: number; dx: number; dy: number }) {
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return <path className="mn-startmark" d="M-0.09,-0.17 L0.09,0 L-0.09,0.17" transform={`translate(${x} ${y}) rotate(${deg.toFixed(1)})`}
               fill="none" stroke="#ffffffe6" strokeWidth={0.09} strokeLinecap="round" strokeLinejoin="round" />;
}

/** پهنای قابِ تخته‌ی آرت (خانه) — همان FRAME ِ tools/mench_board_art.py. */
const MN_FRAME = 0.6;

function MenchBoard({ m, c }: { m: MenchLocal; c: BoardOnline }) {
  const fly = useSignal(c.mnFly);
  const bonk = useSignal(c.mnBonk);
  const thrown = useSignal(c.mnThrow);
  const walk = useSignal(c.mnWalk);
  const poseFx = useSignal(c.mnPose);
  const poseOn = useActive(poseFx?.until ?? 0);
  // رو به کدام سو می‌دود: گام‌های آرت رو به راست‌اند؛ رفتن به چپ آینه می‌شود، گامِ عمودی سوی قبلی را نگه می‌دارد
  const facing = useRef(new Map<string, number>());
  const fx = useSignal(c.mnFx);
  const shake = useSignal(c.mnShake);
  const svg = useRef<SVGSVGElement>(null);
  useEffect(() => {
    // لرزشِ تخته سرِ ضربه (فقط وقتی خودت زدی یا زده شدی) — با animate تا کلاسِ ری‌اکت دست نخورد
    const el = svg.current;
    if (!shake || !el || typeof el.animate !== "function") return;
    if (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const a = el.animate([
      { transform: "translate(0, 0)" }, { transform: "translate(-1.4%, 0.5%) rotate(-0.6deg)" },
      { transform: "translate(1.3%, -0.4%) rotate(0.5deg)" }, { transform: "translate(-0.8%, 0.3%)" },
      { transform: "translate(0.5%, 0)" }, { transform: "translate(0, 0)" },
    ], { duration: ms(420), easing: "ease-out" });
    return () => a.cancel();
  }, [shake]);

  const color = (corner: number) => menchColor(corner, m.shift);
  // گوشه‌های بی‌بازیکن (دو و سه نفره): خانه‌ی کم‌رنگ، بی رنگ و نشانِ شروع — ولی با «خانه‌ی امن»
  // خانه‌ی شروعشان هنوز امن است و ستاره‌اش را دارد
  const used = new Set(m.corner);
  const safe = m.opt.safe;
  // دیوار: دو مهره‌ی یک صندلی روی یک خانه‌ی حلقه («دیوار» روشن)
  const wall = (s: number, p: number) => m.opt.pair && p >= 0 && p <= MAIN_LAST && (m.pos[s]?.filter((q) => q === p).length ?? 0) >= 2;

  // مهره‌های هم‌خانه: دیوار روی هم وسط، بقیه دورِ خانه — تا همه دیده شوند
  const keyOf = (s: number, t: number, p: number) =>
    p === BASE ? `b${s}_${t}` : p <= MAIN_LAST ? `L${((m.corner[s] ?? 0) * 13 + p) % 52}` : `H${m.corner[s]}_${p}`;
  const groups = new Map<string, { s: number; t: number }[]>();
  for (let s = 0; s < m.n; s++) for (let t = 0; t < 4; t++) {
    const p = m.pos[s]![t]!;
    if (p === MENCH_NONE) continue;   // «سریع»: مهره‌ی بیرون از بازی کشیده نمی‌شود
    const k = keyOf(s, t, p);
    const g = groups.get(k);
    if (g) g.push({ s, t }); else groups.set(k, [{ s, t }]);
  }
  const offset = new Map<string, [number, number]>();
  const small = new Set<string>();   // کنارِ دیوار: کوچک‌تر (۰٫۷۸)
  const walls: { x: number; y: number; color: string; key: string }[] = [];
  for (const [k, g] of groups) {
    if (g.length < 2) continue;
    const ws = g.filter(({ s, t }) => wall(s, m.pos[s]![t]!));
    const rest = g.filter((e) => !ws.includes(e));
    if (ws.length >= 2) {
      const { s, t } = ws[0]!;
      const [x, y] = menchTokenXY(m.corner[s] ?? 0, t, m.pos[s]![t]!);
      // دو آدمک روی آجر: اولی پایین‌تر و جلو، دومی ۰٫۲۶ خانه بالاتر — همان MenchView ِ اپ
      const dx = rest.length > 0 ? -0.16 : 0;
      ws.forEach((e, i) => offset.set(`${e.s}-${e.t}`, [dx, i === 0 ? 0.13 : -0.13]));
      walls.push({ x: x + dx, y, color: color(m.corner[s] ?? 0), key: k });
      rest.forEach((e, i) => { offset.set(`${e.s}-${e.t}`, [0.3, rest.length === 1 ? 0 : -0.18 + (0.36 * i) / (rest.length - 1)]); small.add(`${e.s}-${e.t}`); });
    } else {
      g.forEach((e, i) => offset.set(`${e.s}-${e.t}`, [Math.cos((i * 2 * Math.PI) / g.length) * 0.2, Math.sin((i * 2 * Math.PI) / g.length) * 0.2]));
    }
  }

  // ژستِ هر آدمک: در حالِ راه (گام‌ها یکی‌درمیان، بیرون‌آمدن از خانه پرش)، لحظه‌ی بازی (ترسِ زده‌شدن،
  // شادیِ رسیدن)، برنده‌ی پایان شاد؛ وگرنه ایستاده و آرام نفس می‌کشد
  const poseOf = (s: number, t: number): MenchPose => {
    if (walk && walk.seat === s && walk.tok === t) return walk.base ? "jump" : walk.step % 2 === 1 ? "step1" : "step2";
    if (poseOn && poseFx?.key === `${s}-${t}`) return poseFx.pose;
    if (m.over && (m.rank[s] ?? 0) === 1) return "cheer";
    return "idle";
  };
  const pieces: { s: number; t: number; x: number; y: number; ord: number }[] = [];
  for (let s = 0; s < m.n; s++) for (let t = 0; t < 4; t++) {
    const p = m.pos[s]![t]!;
    if (p === MENCH_NONE) continue;
    let [x, y] = menchTokenXY(m.corner[s] ?? 0, t, p);
    const o = offset.get(`${s}-${t}`);
    if (o) { x += o[0]; y += o[1]; }
    pieces.push({ s, t, x, y, ord: pieces.length });
  }
  // آدمک‌ها ایستاده‌اند: پایین‌تری جلوتر است
  pieces.sort((a, b) => a.y - b.y || a.ord - b.ord);
  const tokens: ReactElement[] = pieces.map(({ s, t, x, y }) => {
    // قاعده‌ی §4.5: مهره‌ی فهرست‌شده در خانه یا روی دیوار = همه‌ی هم‌جاهایش روشن، لمسِ هر کدام همان را می‌فرستد
    const send = menchPickable(m, s, t);
    const legal = send >= 0;
    // در پرواز، یا در صحنه‌ی چوبدستی (هر دو را خودِ صحنه می‌کشد)
    const away = (!!fly && fly.seat === s && fly.tok === t)
      || (!!bonk && ((bonk.seat === s && bonk.tok === t) || (bonk.vseat === s && bonk.vtok === t)));
    const pose = poseOf(s, t);
    const ci = pieceIdx(m.corner[s] ?? 0, m.shift);
    const walking = !!walk && walk.seat === s && walk.tok === t;
    const motion = walking ? (walk!.base ? "jump" : `walk${walk!.step % 2}`) : legal ? "hop" : pose === "cheer" ? "cheer" : pose === "scared" ? "scared" : "idle";
    let face = 1, lean = 0;
    if (walking) {
      const p = m.pos[s]![t]!, corner = m.corner[s] ?? 0;
      const [px] = menchTokenXY(corner, t, walk!.base ? BASE : p - 1), [cx] = menchTokenXY(corner, t, p);
      const dx = cx - px;
      if (Math.abs(dx) > 0.05) { facing.current.set(`${s}-${t}`, Math.sign(dx)); lean = 7 * Math.sign(dx); }
      face = facing.current.get(`${s}-${t}`) ?? 1;
    } else facing.current.delete(`${s}-${t}`);
    const k = small.has(`${s}-${t}`) ? 0.78 : 1;
    return (
      <g key={`${s}-${t}`} className={`bt-tok${m.pos[s]![t] === BASE ? " home" : ""}${legal ? " legal" : ""}${away ? " mn-away" : ""}`}
         style={{ transform: `translate(${x}px, ${y + PIECE_FEET}px)` }} data-pose={pose}
         onClick={legal ? () => c.pick(t, s) : undefined} pointerEvents={legal ? undefined : "none"}
         role={legal ? "button" : undefined} aria-label={legal ? `مهره‌ی ${fa(t + 1)}` : undefined}>
        <g transform={k !== 1 ? `scale(${k})` : undefined}>
          {legal && <ellipse className="bt-glow" rx={0.58} ry={0.25} fill="#ffe36a" opacity={0.8} />}
          <ellipse rx={0.35} ry={0.12} fill="#000" opacity={0.3} />
          <g className={`mn-body ${motion}`} style={motion === "idle" ? { "--ph": `${-(((s * 4 + t) * 1.7) / 2.3) % 2.73}s` } as CSSProperties : undefined}>
            <g transform={face < 0 || lean ? `rotate(${lean}) scale(${face} 1)` : undefined}><PieceArt ci={ci} pose={pose} girl={m.seats[s]?.piece === 1} /></g>
          </g>
          {legal && <rect x={-0.62} y={-1.6} width={1.24} height={1.9} fill="transparent" />}
        </g>
      </g>
    );
  });
  // گوشه‌ها: مرکزِ هر خانه‌ی گوشه (پس از چرخش)
  const yardAt = (k: number) => menchXY(k === 0 || k === 3 ? 2.5 : 11.5, k === 0 || k === 1 ? 2.5 : 11.5);
  const cellRect = (x: number, y: number, key: string) => <rect key={key} x={x - 0.47} y={y - 0.47} width={0.94} height={0.94} rx={0.14} />;
  return (
    <>
    <svg ref={svg} viewBox={`${-MN_FRAME} ${-MN_FRAME} ${15 + 2 * MN_FRAME} ${15 + 2 * MN_FRAME}`} className={`bt-svg mn-board mn-th${m.theme}`}
         role="img" aria-label={`تخته‌ی منچ — ${MENCH_THEMES[m.theme]}`} data-theme={m.theme}>
      {/* تخته‌ی آرت (tools/mench_board_art.py): یک تصویر برای رنگِ قرمز پایین-چپ؛ تخته چهارسو متقارن است، پس
          چرخشِ ۹۰ × shift درجه (پادساعت‌گرد) رنگِ من را پایین-چپ می‌نشاند */}
      <image className="mn-art" href={`/art/mench/board${m.theme}.svg`} x={-MN_FRAME} y={-MN_FRAME} width={15 + 2 * MN_FRAME}
             height={15 + 2 * MN_FRAME} preserveAspectRatio="none" transform={m.shift ? `rotate(${-90 * m.shift} 7.5 7.5)` : undefined} />
      {[0, 1, 2, 3].filter((k) => !used.has(k)).map((k) => {
        // گوشه‌ی بی‌بازیکن (دو و سه نفره): پرده‌ای به رنگِ تم روی خانه، شروع، راهرو و مثلثِ مقصدش
        const [x, y] = yardAt(k);
        const [sx, sy] = menchXY(...MENCH_LOOP[k * 13]!);
        const [hx, hy] = menchXY(...MENCH_HOME[k]![5]!);
        const dx = Math.sign(hx - 7.5), dy = Math.sign(hy - 7.5);
        const tri = `7.5,7.5 ${7.5 + 1.5 * dx - 1.5 * dy},${7.5 + 1.5 * dy + 1.5 * dx} ${7.5 + 1.5 * dx + 1.5 * dy},${7.5 + 1.5 * dy - 1.5 * dx}`;
        return (
          <g key={`v${k}`} className="mn-veil" data-corner={k}>
            <rect x={x - 2.92} y={y - 2.92} width={5.84} height={5.84} rx={0.5} />
            {cellRect(sx, sy, "s")}
            {MENCH_HOME[k]!.slice(0, 5).map(([a, b], h) => { const [cx, cy] = menchXY(a, b); return cellRect(cx, cy, `h${h}`); })}
            <polygon points={tri} />
          </g>
        );
      })}
      {MENCH_LOOP.map(([a, b], i) => {
        const [x, y] = menchXY(a, b);
        const start = i % 13 === 0, k = i / 13;
        const mine = start && used.has(k);
        if (safe && MENCH_SAFE.includes(i))
          // «خانه‌ی امن»: ستاره روی هشت خانه — سفید روی شروعِ رنگی، رنگِ ستاره‌ی تم روی بقیه
          return <polygon key={`l${i}`} className={`mn-star${mine ? " start" : ""}`} points={starPoints(x, y, start ? 0.3 : 0.34)}
                          fill={mine ? "#ffffffe6" : "#e0b23a"} />;
        if (!mine) return null;
        const [nx, ny] = menchXY(MENCH_LOOP[(i + 1) % 52]![0], MENCH_LOOP[(i + 1) % 52]![1]);
        return <StartMark key={`l${i}`} x={x} y={y} dx={nx - x} dy={ny - y} />;
      })}
      {m.opt.power && m.cells.map((k, i) => {
        // «نیروها»: خانه‌ی درخشان — پُر با نشانِ نیرو و حلقه‌ی تپنده، خالی با حلقه‌ی کم‌رنگ
        const [x, y] = menchPowerXY(i, m.shift);
        return (
          <g key={`pw${i}`} className={`mn-pw${k ? "" : " empty"}`} transform={`translate(${x} ${y})`} pointerEvents="none"
             data-power={k} aria-label={k ? `خانه‌ی درخشان: ${POWER_FA[k as 1 | 2 | 3]}` : undefined}>
            <circle className="mn-pw-ring" r={0.4} fill="none" />
            {k > 0 && <PowerGlyph kind={k} />}
          </g>
        );
      })}
      {m.opt.hunter && m.corner.map((k, s) => {
        if (m.gate[s] || m.rank[s]) return null;
        const [a, b] = MENCH_HOME[k]![0]!;
        const [x, y] = menchXY(a, b);
        return <GateLock key={`lk${s}`} x={x} y={y} />;
      })}
      {walls.map((w) => (
        // نشانِ دیوار: دو ردیف آجر زیرِ دو مهره‌ی روی هم
        <g key={`w${w.key}`} className="mn-wall" transform={`translate(${w.x} ${w.y + 0.56})`} pointerEvents="none">
          <rect x={-0.3} y={-0.07} width={0.6} height={0.14} rx={0.03} fill="#8a4b2a" stroke={w.color} strokeWidth={0.03} />
          <line x1={-0.1} y1={-0.07} x2={-0.1} y2={0.07} stroke="#f4ead0" strokeWidth={0.025} />
          <line x1={0.1} y1={-0.07} x2={0.1} y2={0.07} stroke="#f4ead0" strokeWidth={0.025} />
        </g>
      ))}
      {tokens}
      <MenchGhosts m={m} c={c} />
      {bonk && <MenchBonk key={bonk.id} b={bonk} />}
      {fly && (
        // پروازِ چرخانِ زده‌شده از خانه‌ی فرود تا جایش در خانه
        <g key={fly.id} className="mn-fly" pointerEvents="none"
           style={{ "--fx0": `${fly.from[0]}px`, "--fy0": `${fly.from[1]}px`, "--fx1": `${fly.to[0]}px`, "--fy1": `${fly.to[1]}px`, "--fly": `${fly.ms}ms`,
             transform: `translate(${fly.to[0]}px, ${fly.to[1]}px)` } as CSSProperties}>
          <g className="mn-fly-i">
            <g transform={`translate(0 ${PIECE_FEET})`}><PieceArt ci={Math.max(0, (MENCH_COLORS as readonly string[]).indexOf(fly.color))} pose="fallen" girl={m.seats[fly.seat]?.piece === 1} /></g>
          </g>
        </g>
      )}
      {fx && <MenchFx key={fx.id} fx={fx} />}
    </svg>
    {thrown && <ThrownDie key={thrown.id} th={thrown} skin={m.seats[thrown.seat]?.dice} />}
    </>
  );
}

/** جای نقطه‌ی (u خانه) روی صحنه به درصد — تخته‌ی آرت ۱۵ + ۲ × MN_FRAME خانه است. */
const boardPct = (u: number) => `${(((u + MN_FRAME) / (15 + 2 * MN_FRAME)) * 100).toFixed(3)}%`;
const menchPlace = (x: number, y: number): [string, string] => [boardPct(x), boardPct(y)];

/**
 * تاسِ پرتابی روی تخته (diceThrow.ts؛ کاربر: «بازیکن درگیرِ تاس باشد»): از کنارِ گوشه‌ی صاحبِ نوبت با سرعتِ v
 * از دست می‌افتد و در هوا می‌چرخد، صاف فرود می‌آید، یک جهش، و لبه‌به‌لبه می‌غلتد (planRoll) — به قاب می‌خورد و
 * برمی‌گردد («تق» و کمی له‌شدن)، روی لبه‌ی آخر کمی می‌ماند و صاف روی عدد می‌افتد، کمی پس می‌نشیند و می‌ایستد؛ بعد
 * hold می‌ماند و محو می‌شود. جابه‌جایی با requestAnimationFrame روی ref ها.
 */
/**
 * place: واحدِ پرتاب (تخته‌ی منچ، ۰..۱۵) → درصدِ جا روی صحنه؛ size: ضلعِ تاس (CSS)؛ sound: صدای پرتاب (دو تاسِ مارپله و
 * تخته‌نرد یک صدا دارند، با تاسِ اول). th.dx: جابه‌جاییِ نقطه‌ی رهاشدن (دو تاس از کنارِ هم). field: زمینِ پرتاب (تخته‌نرد
 * پهن است).
 */
function ThrownDie({ th, place = menchPlace, size = "calc(100cqw * 1.45 / 16.2)", sound = true, field = FIELD15, skin }: {
  th: { corner: number; v: [number, number]; value: number; ms: number; hold: number; id: number; dx?: number };
  place?: (x: number, y: number) => [string, string]; size?: string; sound?: boolean; field?: ThrowField;
  /** تاسِ کسی که انداخته (Seat.dice) — diceSkins.ts؛ خالی = عاج. */
  skin?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null), hop = useRef<HTMLSpanElement>(null), cube = useRef<HTMLSpanElement>(null), shadow = useRef<HTMLSpanElement>(null);
  const [sx, y0] = throwStart(th.corner, field);
  const x0 = sx + (th.dx ?? 0);
  useEffect(() => {
    const T = th.ms / 1000;
    const path = throwPath(x0, y0, th.v[0], th.v[1], T, THROW_R, field.w - THROW_R, field.h - THROW_R);
    // در هوا می‌چرخد، صاف فرود می‌آید و لبه‌به‌لبه می‌غلتد تا غلتِ آخر روی عدد بیفتد (diceThrow.ts، planRoll)
    const plan = planRoll(path, th.value);
    let squash = 0;
    // صدا: یک پرتابِ کاملِ ضبط‌شده (diceSfx)، کمی پیش از فرود خبر می‌شود تا اولین تقش درست سرِ فرود بنشیند
    let rang = !sound;
    const walls = path.bounces.map((b) => sampleAt(path, b).roll);
    const until = Math.max(T, plan.tEnd + SETTLE_WOBBLE);
    let landed = 0, tips = 0;
    const t0 = performance.now();
    let raf = 0;
    const frame = () => {
      const el = wrap.current, h = hop.current, c = cube.current, sh = shadow.current;
      if (!el || !h || !c || !sh) return;
      const t = Math.min(until, (performance.now() - t0) / 1000), p = t / T;
      const r = rollAt(plan, t);
      [el.style.left, el.style.top] = place(r.x, r.y);
      c.style.transform = qCss(r.q);
      if (!rang && t >= plan.tLand - 0.05) { rang = true; diceSfx({ hitAt: (plan.tLand - t) * 1000 }); }
      if (landed === 0 && t >= plan.tLand) { landed = 1; squash = 1; }
      else if (landed === 1 && p >= 0.38) { landed = 2; squash = 0.6; }
      while (walls.length && walls[0]! <= r.s) { walls.shift(); squash = 1; }
      if (r.tips > tips) { tips = r.tips; squash = Math.max(squash, 0.4); }
      squash = Math.max(0, squash - 0.12);
      const lift = r.lift;
      h.style.transform = `translateY(${(-lift * 70).toFixed(2)}%) scale(${(1 + 0.32 * lift + 0.08 * squash).toFixed(3)}, ${(1 + 0.32 * lift - 0.1 * squash).toFixed(3)})`;
      sh.style.transform = `scale(${(1 - 0.35 * lift).toFixed(3)})`;
      sh.style.opacity = (0.9 - 0.5 * lift).toFixed(3);
      if (t < until) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // یک پرتاب برای هر id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [th.id]);
  return (
    <div className="mn-throw" ref={wrap} aria-hidden="true"
         style={{ left: place(x0, y0)[0], top: place(x0, y0)[1], "--dur": `${th.ms}ms`, "--hold": `${th.hold}ms` } as CSSProperties}>
      <span className="d3" style={{ "--s": size, ...diceVars(skin) } as CSSProperties}>
        <span className="d3-shadow" ref={shadow} />
        <span className="d3-hop" ref={hop}><DieCube cubeRef={cube} /></span>
      </span>
    </div>
  );
}

/**
 * صحنه‌ی زدن (کاربر، ۲۵ اکتبر ۲۰۲۶): زننده کنارِ زده‌شده می‌ایستد و سه قابِ آرتِ کاربر پشتِ هم می‌آیند — چوب
 * پشتِ کمر، چوب بالای سر (کمی عقب می‌کشد)، ضربه بر سرِ زده‌شده (BonkArt)؛ میانِ قاب‌ها خم‌شدن و کش‌آمدنِ تن
 * پیوسته است و ردِ چوب در هوا می‌ماند، تا سه قاب نرم دیده شوند. «تق!» و ستاره‌ها می‌پرند و زده‌شده له می‌شود —
 * بعد کنترلر پروازِ خانه (mn-fly) را از جای او شروع می‌کند. همه‌ی زمان‌بندی از --bk (keyframes ِ bk* در
 * style.css)؛ ضربه در ۶۰٪ (BONK_IMPACT).
 */
function MenchBonk({ b }: { b: { x: number; y: number; ci: number; vci: number; ms: number; girl: boolean; vgirl: boolean } }) {
  return (
    <g className="mn-bonk" style={{ "--bk": `${b.ms}ms` } as CSSProperties} transform={`translate(${b.x} ${b.y + PIECE_FEET})`} pointerEvents="none">
      {/* زده‌شده زیر: سرِ چوبِ قابِ ضربه رویش می‌نشیند */}
      <g transform={`translate(${BONK_DX} 0)`}>
        <ellipse rx={0.35} ry={0.12} fill="#000" opacity={0.3} />
        <g className="bk-vic"><PieceArt ci={b.vci} pose="scared" girl={b.vgirl} /></g>
      </g>
      <g transform={`translate(${-BONK_DX} 0)`}>
        <ellipse rx={0.35} ry={0.12} fill="#000" opacity={0.3} />
        <g className="bk-att">
          {[1, 2, 3].map((f) => <g key={f} className={`bk-f${f}`}><BonkArt ci={b.ci} f={f} girl={b.girl} /></g>)}
        </g>
        {/* ردِ چرخیدنِ چوب: از سرِ چوبِ بالای سر (قابِ ۲) تا سرِ چوبِ ضربه (قابِ ۳) */}
        <path className="bk-swoosh" d="M -0.1 -1.68 A 0.8 0.8 0 0 1 0.7 -1.02" pathLength={1} fill="none"
              stroke="#fffbe8" strokeWidth={0.08} strokeLinecap="round" />
      </g>
      <g transform={`translate(${BONK_DX - 0.05} -1.42)`}>
        <g className="bk-pow">
          <polygon points={starPoints(0, 0, 0.42, 10, 0.72)} fill="#fff6c4" stroke="#3a2600" strokeWidth={0.04} strokeLinejoin="round" />
          <text y={0.09} textAnchor="middle" fontSize={0.26} fontWeight={900} fill="#c0392b">تق!</text>
        </g>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i * Math.PI * 2) / 6 + 0.3;
          return <polygon key={i} className="bk-star" points={starPoints(0, 0, 0.1, 5)} fill="#ffd84a"
                          style={{ "--sx": `${(Math.cos(a) * 0.7).toFixed(3)}px`, "--sy": `${(Math.sin(a) * 0.55).toFixed(3)}px` } as CSSProperties} />;
        })}
      </g>
    </g>
  );
}

/**
 * قابِ صحنه‌ی زدن (tools/mench_piece_cut.py stick): ۳۶۰×۳۶۰ با همان مقیاسِ پیکسلِ آدمک (۲٫۵۵ خانه)، کفِ پا ۱۰px
 * بالای لبه‌ی پایین و وسطِ افقی. ۱ چوب پشتِ کمر، ۲ چوب بالای سر، ۳ ضربه (سرِ چوب ~۰٫۷ خانه جلوتر، هم‌قدِ سر).
 */
const BONK_ART = (PIECE_H * 360) / 240;
function BonkArt({ ci, f, girl }: { ci: number; f: number; girl: boolean }) {
  return <image href={`/art/mench/${girl ? "g" : "p"}${ci}-bonk${f}.webp`} x={-BONK_ART / 2} y={-BONK_ART * (1 - 10 / 360)}
                width={BONK_ART} height={BONK_ART} preserveAspectRatio="none" />;
}

/**
 * شبحِ هر حرکتِ مجاز: نقطه‌ی کم‌رنگ به رنگِ صاحبِ مهره سرِ خانه‌ی فرود، با **یک** نشان —
 * بالاترین از زدن، رسیدن، خطر، دیوار، امن. شبح خودش دکمه است و همان مهره را می‌فرستد.
 * شبح‌های هم‌خانه یکی می‌شوند و نشانِ بالاتر می‌ماند. بی دُمِ نسخه‌ی ۲ هیچ.
 */
function MenchGhosts({ m, c }: { m: MenchLocal; c: BoardOnline }) {
  if (!m.v2 || m.hints.length === 0 || m.tokens.length === 0) return null;
  const oc = m.corner[m.owner] ?? 0;
  const col = menchColor(oc, m.shift);
  const by = new Map<number, { h: MenchHint; b: MenchBadge }>();
  for (const h of m.hints) {
    if (h.target < 0 || h.target > GOAL) continue;
    const b = menchBadge(h, m.opt, oc);
    const cur = by.get(h.target);
    if (!cur || menchBadgeRank(b) > menchBadgeRank(cur.b)) by.set(h.target, { h, b });
  }
  return (
    <g className="mn-ghosts">
      {[...by.values()].map(({ h, b }) => {
        const [x, y] = menchTokenXY(oc, h.tok, h.target);
        return (
          <g key={`g${h.target}`} className={`mn-ghost${b ? ` mn-g-${b}` : ""}`} transform={`translate(${x} ${y})`}
             data-tok={h.tok} role="button" aria-label={`مهره‌ی ${fa(h.tok + 1)} به این خانه${b ? ` — ${BADGE_FA[b]}` : ""}`}
             onClick={() => c.pick(h.tok, m.owner)}>
            <circle r={0.5} fill="transparent" />
            <circle className="mn-ghost-dot" r={0.34} fill={col} fillOpacity={0.4} stroke="#ffffff" strokeWidth={0.05} strokeDasharray="0.12 0.08" />
            {b && <BadgeIcon kind={b} />}
          </g>
        );
      })}
    </g>
  );
}

/** نشانِ کوچکِ شبح (کمتر از نیم خانه) — گوشه‌ی بالای شبح. */
function BadgeIcon({ kind }: { kind: Exclude<MenchBadge, ""> }) {
  return (
    <g className={`mn-badge mn-b-${kind}`} transform="translate(0.24 -0.24)" pointerEvents="none">
      <circle r={0.2} fill="#14122be6" />
      {kind === "capture" && (
        <g stroke="#ff5a48" strokeWidth={0.045} fill="none">
          <circle r={0.1} />
          <path d="M0,-0.17 V-0.06 M0,0.06 V0.17 M-0.17,0 H-0.06 M0.06,0 H0.17" />
        </g>
      )}
      {kind === "goal" && (
        <g>
          <path d="M-0.08,0.14 V-0.14" stroke="#fff" strokeWidth={0.04} />
          <path d="M-0.08,-0.14 L0.13,-0.08 L-0.08,-0.01 Z" fill="#f5c451" />
        </g>
      )}
      {kind === "danger" && (
        <g>
          <circle r={0.14} fill="#e2503a" />
          <text y={0.075} textAnchor="middle" fontSize={0.22} fontWeight={700} fill="#fff">!</text>
        </g>
      )}
      {kind === "pair" && (
        <g fill="#c7743f" stroke="#f4ead0" strokeWidth={0.02}>
          <rect x={-0.13} y={-0.02} width={0.26} height={0.09} />
          <rect x={-0.13} y={-0.11} width={0.26} height={0.09} />
        </g>
      )}
      {kind === "safe" && <polygon points={starPoints(0, 0, 0.14)} fill="#e0b23a" />}
    </g>
  );
}

/** جلوه‌ی لحظه: جرقه‌ی بیرون‌آمدن از خانه، برقِ رسیدنِ دیگری. */
function MenchFx({ fx }: { fx: { kind: "burst" | "sparkle"; x: number; y: number; color: string } }) {
  const rays = fx.kind === "burst" ? 8 : 6;
  return (
    <g className={`mn-fx mn-fx-${fx.kind}`} transform={`translate(${fx.x} ${fx.y})`} pointerEvents="none">
      <circle className="mn-ring" r={0.45} fill="none" stroke={fx.kind === "burst" ? fx.color : "#ffe36a"} strokeWidth={0.08} />
      {Array.from({ length: rays }, (_, i) => {
        const a = (i * 2 * Math.PI) / rays;
        return <circle key={i} className="mn-ray" r={0.07} fill={fx.kind === "burst" ? fx.color : "#fff6c4"}
                       style={{ "--dx": `${(Math.cos(a) * 0.75).toFixed(3)}px`, "--dy": `${(Math.sin(a) * 0.75).toFixed(3)}px` } as CSSProperties} />;
      })}
    </g>
  );
}

/** کاغذرنگی — فقط رتبه‌ی اولِ خودت و پایانِ برده (جشنِ «میانه‌رو» ِ §12). */
function Confetti({ c }: { c: BoardOnline }) {
  const id = useSignal(c.confetti);
  const [live, setLive] = useState(0);
  useEffect(() => {
    if (!id) return;
    setLive(id);
    const t = setTimeout(() => setLive(0), ms(2800));
    return () => clearTimeout(t);
  }, [id]);
  if (!live) return null;
  const colors = ["#d9433a", "#46a24b", "#e0b23a", "#3f7fd4", "#fff3c4"];
  return (
    <div className="mn-confetti" key={live} aria-hidden="true">
      {Array.from({ length: 30 }, (_, i) => (
        <i key={i} style={{ "--x": `${(i * 37) % 100}%`, "--d": `${(i % 8) * 70}ms`, "--c": colors[i % colors.length],
          "--r": `${(i * 53) % 360}deg`, "--s": `${0.8 + (i % 3) * 0.25}` } as CSSProperties} />
      ))}
    </div>
  );
}

/**
 * پایانِ منچ با دُمِ نسخه‌ی ۲: رتبه‌ها و آمار. بردن = رتبه‌ی خودم ۱ (تیمی: رتبه‌ی تیم).
 * بی «تا نفرِ آخر» فقط اولی «برنده» است و بقیه «n مهره رسیده، x٪ راه» (بی ترتیب‌نویسی)؛
 * با «تا نفرِ آخر» ترتیب؛ تیمی نتیجه‌ی تیم.
 */
function MenchOver({ m, c }: { m: MenchLocal; c: BoardOnline }) {
  const won = menchWon(m);
  const teams = m.opt.teams && m.n === 4;
  const places = m.opt.places && !teams;
  const w = m.winner >= 0 ? m.winner : m.rank.findIndex((r) => r === 1);
  const who = w >= 0 ? c.seatName(w) : "";
  const mine = m.rank[0] ?? 0;
  const title = won ? (teams ? "تیم شما برد!" : "همه‌ی مهره‌ها رسیدند — بردی!")
    : teams && w >= 0 ? `${who} و ${c.seatName(menchPartner(w))} بردند`
    : places && !m.watching && mine > 0 ? `نفر ${menchOrdinal(mine)} شدی. ${who} برد`
    : `${who} برد`;
  const order = Array.from({ length: m.n }, (_, s) => s)
    .sort((a, b) => ((m.rank[a] || 99) - (m.rank[b] || 99)) || a - b);
  const goals = (s: number) => m.pos[s]?.filter((p) => p === GOAL).length ?? 0;
  const result = (s: number): string => {
    const r = m.rank[s] ?? 0;
    if (teams) return r === 1 ? "تیم برنده" : "تیم دوم";
    if (places) return r > 0 ? `نفر ${menchOrdinal(r)}` : "";
    return r === 1 || s === m.winner ? "برنده" : `${fa(goals(s))} مهره رسیده، ${fa(menchProgress(m, s))}٪ راه`;
  };
  return (
    <Overlay label="پایان بازی" card={m.watching ? "even" : won ? "win" : "lose"}
             after={<ResultAd won={m.watching ? null : won} watching={m.watching} />}>
      <div className="vt">{title}</div>
      <div className="mn-end" role="table" aria-label="نتیجه">
        {order.map((s) => (
          <div key={s} role="row" className={`mn-endrow${(m.rank[s] ?? 0) === 1 ? " win" : ""}${s === 0 && !m.watching ? " me" : ""}`}>
            <i className="dot" style={{ background: menchColor(m.corner[s] ?? 0, m.shift) }} aria-hidden="true" />
            <b role="cell">{c.seatName(s)}</b>
            <span role="cell" className="res">{result(s)}</span>
            <span role="cell" className="st">زد {fa(m.hits[s] ?? 0)}، زده شد {fa(m.lost[s] ?? 0)}، شش {fa(m.sixes[s] ?? 0)}</span>
          </div>
        ))}
      </div>
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {m.watching ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}

// ─────────────────────────── دوز ───────────────────────────

function Dooz({ m, c }: { m: DoozLocal; c: BoardOnline }) {
  const colorName = (s: number) => (doozColor(s, m.shift) === doozColor(0, 0) ? "مهره‌های قرمز" : "مهره‌های طلایی");
  const status = m.mine ? "ستونی را لمس کن" : m.over ? "" : m.watching ? `نوبت ${c.seatName(m.turn)}` : `${c.seatName(m.turn)} فکر می‌کند…`;
  return (
    <>
      <Mid ar={7.4 / 6.95} rows={140}
        top={<div className="bt-row top single">
          <Plate m={m} seat={1} color={doozColor(1, m.shift)} sub={colorName(1)} />
        </div>}
        me={<div className="bt-row me single">
          <Plate m={m} seat={0} color={doozColor(0, m.shift)} sub={colorName(0)} />
        </div>}>
        <DoozBoard m={m} c={c} />
      </Mid>
      <div className="bt-act">
        <Status text={status} />
        <Timer c={c} />
        {!m.watching && <ChatFab inline />}
      </div>
    </>
  );
}

function DoozBoard({ m, c }: { m: DoozLocal; c: BoardOnline }) {
  const dropping = useSignal(c.dropping);
  const onLine = (col: number, row: number) => m.line.some(([a, b]) => a === col && b === row);
  // پایان با چهارتا: بقیه‌ی مهره‌ها کم‌رنگ می‌شوند و خطی به رنگِ برنده از رویِ چهارتا کشیده می‌شود
  const won = m.over && m.line.length > 0;
  const discs: ReactElement[] = [];
  for (let col = 0; col < DOOZ_W; col++) for (let row = 0; row < DOOZ_H; row++) {
    const s = m.cells[col]![row]!;
    if (s < 0) continue;
    const x = col + 0.5, y = DOOZ_H - 1 - row + 0.5;
    const fall = dropping === `${col}:${row}`;
    const cls = [fall ? "bt-fall" : "", won && !onLine(col, row) ? "dz-dim" : ""].filter(Boolean).join(" ");
    discs.push(
      <g key={`${col}:${row}`} className={cls || undefined}
         style={fall ? ({ "--fall": `${-(DOOZ_H - row + 0.4)}px` } as CSSProperties) : undefined}>
        <circle cx={x} cy={y} r={0.39} fill={doozColor(s, m.shift)} stroke="#00000044" strokeWidth={0.05} />
        <circle cx={x - 0.1} cy={y - 0.12} r={0.13} fill="#ffffff55" />
        {m.over && onLine(col, row) && <circle cx={x} cy={y} r={0.44} fill="none" stroke="#ffffff" strokeWidth={0.08} className="bt-win" />}
      </g>,
    );
  }
  const mineColor = doozColor(0, m.shift);
  return (
    <svg viewBox="-0.2 -0.75 7.4 6.95" className="bt-svg" role="img" aria-label="تخته‌ی دوز">
      {m.mine && m.cols.map((col) => <circle key={`hint${col}`} cx={col + 0.5} cy={-0.42} r={0.13} fill={mineColor} opacity={0.7} />)}
      <rect x={-0.15} y={-0.15} width={7.3} height={6.3} rx={0.35} fill="#172a58" />
      <rect x={0} y={0} width={7} height={6} rx={0.25} fill="#24407e" />
      {Array.from({ length: DOOZ_W * DOOZ_H }, (_, i) => {
        const col = Math.floor(i / DOOZ_H), row = i % DOOZ_H;
        return <circle key={`o${i}`} cx={col + 0.5} cy={DOOZ_H - 1 - row + 0.5} r={0.4} fill="#0d1a36" />;
      })}
      {discs}
      {won && <DoozStrike m={m} />}
      {m.mine && Array.from({ length: DOOZ_W }, (_, col) => (
        <rect key={`col${col}`} x={col} y={-0.75} width={1} height={6.75} fill="transparent"
              className={`bt-col${m.cols.includes(col) ? " on" : ""}`}
              onClick={() => c.drop(col)} role="button" aria-label={`ستون ${fa(col + 1)}`} />
      ))}
    </svg>
  );
}

/**
 * خطِ چهارتای برنده: از مرکزِ مهره‌ی یک سر تا سرِ دیگر، به رنگِ برنده با مغزِ سفید و سایه، که کشیده می‌شود — بازنده هم
 * می‌بیند با کدام چهارتا باخت. دو سر همان دو خانه‌ی دورتر از هم‌اند، هر ترتیبی که extra داشته باشد.
 */
function DoozStrike({ m }: { m: DoozLocal }) {
  const pts = m.line.map(([col, row]) => [col + 0.5, DOOZ_H - 1 - row + 0.5] as const);
  let a = pts[0]!, b = pts[0]!, far = -1;
  for (const p of pts) for (const q of pts) {
    const d = (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2;
    if (d > far) { far = d; a = p; b = q; }
  }
  const d = `M ${a[0]} ${a[1]} L ${b[0]} ${b[1]}`;
  return (
    <g className="dz-strike" pointerEvents="none" data-from={`${a[0]},${a[1]}`} data-to={`${b[0]},${b[1]}`}>
      <path d={d} pathLength={1} stroke="#00000070" strokeWidth={0.36} />
      <path d={d} pathLength={1} stroke={doozColor(m.winner, m.shift)} strokeWidth={0.26} />
      <path d={d} pathLength={1} stroke="#ffffff" strokeWidth={0.08} />
    </g>
  );
}

// ─────────────────────────── تخته‌نرد ───────────────────────────

/**
 * میزِ تخته‌نرد روی قابِ منچ و مارپله (کاربر، ۲۷ اکتبر ۲۰۲۶: «تخته را با تغییرهای بازی‌های دیگر جور کن؛ منطقِ بازی و
 * مهره‌ها همان، تاس‌ها را می‌شود عوض کرد»): چهره‌ی گردِ تو پایین-چپ و حریف بالا-راست، هر کدام با جعبه‌ی تاسش رو به
 * میانه. جعبه‌ی خودت دکمه‌ی ریختن است؛ لمس یا کشیدنِ انگشت رویش دو تاس را روی تخته پرتاب می‌کند (ThrownDie)، و بعد
 * تاس‌های نوبت در جعبه‌ی صاحبِ نوبت می‌مانند — جفت چهار تاس، خرج‌شده کم‌رنگ. ایموجی و پیام میانِ دو گوشه‌ی پایین؛
 * واکنش‌ها حبابِ روی چهره‌اند و بعد از زدن نوارِ واکنش جای خطِ وضعیت می‌آید.
 *
 * تخته‌ی افقیِ کلاسیک: خانه‌ی تو پایین-راست (خانه‌های ۱ تا ۶ ِ تو، ۱ کنارِ لبه)، بالا ۱۳ تا ۲۴ از چپ به راست، بار وسط،
 * سینیِ جمع سمتِ راست. هندسه در board/nardGeom.ts است، همان اعدادِ BackgammonView ِ اپ.
 */
function Nard({ m, c }: { m: NardLocal; c: BoardOnline }) {
  const rolling = useSignal(c.rolling);
  const thrown = useSignal(c.nardThrow);
  const hint = useSignal(c.nardHint);
  const hintOn = useActive(hint?.until ?? 0);
  const timer = useSignal(c.timer);
  const reactOn = useActive(useSignal(c.reactUntil));
  const chat = useSeatBubbles(m.n, m.me);
  // زیرِ چهره کوتاه: راهِ مانده، و از اولین مهره‌ی بیرون‌رفته شمارِ بیرون‌ها
  const sub = (s: number) => `${fa(nardPips(m, s))} تا پایان${m.off[s] > 0 ? ` · ${fa(m.off[s])} بیرون` : ""}`;
  const mine = !m.watching && (m.canRoll || m.moves.length > 0);
  const secs = timer && mine ? Math.max(0, Math.ceil(timer.left)) : 0;
  const frac = timer && mine ? Math.max(0, Math.min(1, timer.left / Math.max(0.1, timer.total))) : 1;
  const seatOf = (s: number) => {
    const ss = m.seats[s];
    const me = s === 0 && !m.watching;
    return (
      <RoundSeat name={me ? "تو" : botBare(ss?.name || "حریف", ss?.bot)} avatar={ss?.avatar} country={ss?.country} bot={ss?.bot} me={me} color={nardColor(s, m.shift)}
                 turn={!m.over && m.turn === s} secs={s === 0 ? secs : 0} frac={frac} sub={sub(s)}
                 cls="mn-seat nd-seat" bubble={chat.get(s)} pid={ss?.playerId} />
    );
  };
  // دو گوشه‌ی روبه‌رو: چهره و جعبه‌ی تاسش رو به میانه؛ دو گوشه‌ی دیگر خالی
  const corner = (k: number) => {
    const s = [0, 1].find((i) => nardCorner(i) === k) ?? -1;
    if (s < 0) return <div className={`mn3-seat c${k} empty`} />;
    return (
      <div className={`mn3-seat c${k}`}>
        {seatOf(s)}
        <NardDiceBox m={m} s={s} rolling={rolling && m.turn === s} onRoll={(v) => c.roll(v)} throwing={!!thrown?.seats.includes(s)} />
      </div>
    );
  };
  const dice = m.dice.length ? " · تاس " + m.dice.map(fa).join("،") : "";
  const status = m.canRoll ? "نوبت توست؛ روی تاس بزن" : hintOn && hint ? hint.text
    : m.moves.length > 0 ? `خانه‌ی روشن را بزن، بعد مقصد را${dice}`
    : m.over ? "" : `نوبت ${c.seatName(m.turn)}${dice}`;
  return (
    <>
      <Mid ar={NW / NH} rows={208}
        top={<div className="mn3-row top">{corner(1)}{corner(2)}</div>}
        me={<div className="mn3-row bottom">{corner(0)}<BoardChatButtons onReact={(k) => c.react(k)} />{corner(3)}</div>}>
        <NardBoard m={m} c={c} />
        {thrown && thrown.throws.map((t, i) => (
          <ThrownDie key={`${thrown.id}-${i}`} th={{ corner: t.corner, v: t.v, value: t.value, ms: thrown.ms, hold: thrown.hold, id: thrown.id, dx: t.dx }}
                     place={nardPlace} size={ND_THROWN_DIE} field={NARD_FIELD} sound={i === 0}
                     skin={m.seats[thrown.seats[Math.min(i, thrown.seats.length - 1)] ?? 0]?.dice} />
        ))}
      </Mid>
      <div className="bt-act">
        {reactOn && !m.watching && !m.canRoll && m.moves.length === 0 ? <Reactions c={c} cls="mp-react mn-react" /> : <Status text={status} />}
      </div>
    </>
  );
}

/** واحدِ پرتاب (زمینِ NARD_FIELD) → درصدِ جا روی تخته‌ی تخته‌نرد. */
const nardPlace = (x: number, y: number): [string, string] => {
  const [bx, by] = nardThrowXY(x, y);
  return [`${((bx / NW) * 100).toFixed(3)}%`, `${((by / NH) * 100).toFixed(3)}%`];
};
const ND_THROWN_DIE = `calc(100cqw * ${NDIE} / ${NW})`;
const nardUnitPx = () => nardThrowUnitPx(document.querySelector(".nd-board")?.getBoundingClientRect().width ?? 0);

/**
 * جعبه‌ی تاسِ یک صندلی کنارِ چهره‌اش — همان جعبه‌ی منچ و مارپله: تاس‌های نوبت در جعبه‌ی صاحبِ نوبت (جفت = چهار تاسِ
 * کوچک؛ خرج‌شده کم‌رنگ)، پیش از ریختن کم‌رنگ. جعبه‌ی خودت دکمه‌ی ریختن است و کشیدنِ انگشت رویش تاس‌ها را به همان سو
 * پرتاب می‌کند؛ در طولِ پرتاب جعبه خالی است.
 */
function NardDiceBox({ m, s, rolling, onRoll, throwing }:
  { m: NardLocal; s: number; rolling: boolean; onRoll: (swipe?: [number, number]) => void; throwing: boolean }) {
  const turn = !m.over && m.turn === s;
  const can = s === 0 && !m.watching && m.canRoll;
  const swipe = useSwipeRoll(can, onRoll, nardUnitPx);
  const d1 = m.rolled[0], d2 = m.rolled[1];
  // تاس‌های همین نوبت (حرکت، یا در حالِ ریختن)؛ پیش از ریختن دو تاسِ کم‌رنگ
  const live = (m.phase === 1 || rolling) && d1 > 0;
  const pair = live ? (d1 === d2 ? [d1, d1, d1, d1] : [d1, d2]) : [d1 || 6, d2 || 6];
  const left = [...m.dice];
  const shown = pair.map((d) => {
    const i = live ? left.indexOf(d) : -1;
    if (i >= 0) left.splice(i, 1);
    return { d, used: i < 0 };
  });
  const four = shown.length === 4;
  const label = can ? "تاس بریز"
    : turn && live ? `تاس‌های نوبت: ${shown.filter((x) => !x.used).map((x) => fa(x.d)).join(" و ") || "خرج شد"}` : "تاس";
  return (
    <button className={`mn-dbox ${four ? "four" : "two"}${turn ? " turn" : ""}${can ? " on" : ""}`} disabled={!can} {...swipe} aria-label={label}>
      {turn && !throwing && shown.map((x, i) => (
        <Die3D key={i} value={x.d} rolling={rolling} seed={i + 1} from={s === 0 ? [1, -1] : [-1, 1]} sound={i === 0} skin={m.seats[s]?.dice}
               size={four ? "calc(var(--mn-av, 64px) * .27)" : "calc(var(--mn-av, 64px) * .3)"} faded={x.used && !rolling} />
      ))}
      {can && <span className="tag">بریز</span>}
    </button>
  );
}

/** لبه‌ی هر مهره رنگِ مخالفِ زمینه‌اش را دارد (عاج روی عاج، زرشکی روی گردو) — همان اپ. */
const NARD_RIM_IVORY = "#7a5a2e", NARD_RIM_RED = "#d4a35a", NARD_BRASS = "#c9a050", NARD_GO = "#5cd980", NARD_HIT = "#ff7a59";
const NARD_ART = (n: string) => `/art/nard/${n}.webp`;

/** مهره: سایه، لبه‌ی هم‌تضاد، رویه، حلقه‌ی کنده‌کاری و برق — مرکزش (۰،۰). */
function NardChecker({ color }: { color: string }) {
  const ivory = color === NARD_IVORY, r = NDIA / 2;
  return (
    <>
      <circle cx={0.04} cy={0.09} r={r} fill="#00000066" />
      <circle r={r} fill={ivory ? NARD_RIM_IVORY : NARD_RIM_RED} />
      <circle r={r * 0.85} fill={color} />
      <circle r={r * 0.52} fill="none" stroke={ivory ? "#00000029" : "#ffffff26"} strokeWidth={0.045} />
      <ellipse cx={-r * 0.32} cy={-r * 0.34} rx={r * 0.26} ry={r * 0.2} fill="#fff" opacity={ivory ? 0.5 : 0.28} />
    </>
  );
}

/** شمارِ ستونِ بیش از پنج (یا بارِ بیش از چهار): قرصِ تیره با لبه‌ی برنجی، یکسان روی هر دو رنگ. */
function NardBadge({ x, y, n }: { x: number; y: number; n: number }) {
  return (
    <g pointerEvents="none">
      <circle cx={x} cy={y} r={NDIA * 0.41} fill="#170f0ae6" stroke={NARD_BRASS} strokeWidth={0.05} />
      <text x={x} y={y + 0.2} textAnchor="middle" fontSize={0.58} fontWeight={700} fill="#fff">{fa(n)}</text>
    </g>
  );
}

/**
 * مهره‌ی پرنده، با کمان و بزرگ‌شدنِ نرم (Web Animations) — زده‌شده چرخان و بلندتر، بیرون‌رفته
 * نزدیکِ سینی به پهلو. مدل مهره را از مبدأ برداشته و هنوز در مقصد نگذاشته، پس دو‌تایی دیده نمی‌شود.
 */
function NardFly({ f, color }: { f: { from: [number, number]; to: [number, number]; ms: number; hit: boolean; off: boolean; id: number }; color: string }) {
  const ref = useRef<SVGGElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    const [x0, y0] = f.from, [x1, y1] = f.to;
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const arc = f.hit ? NCHK * 0.9 : Math.min(NCHK * 0.7, dist * 0.3);
    const frames: Keyframe[] = [];
    for (let i = 0; i <= 10; i++) {
      const t = i / 10, s = Math.sin(t * Math.PI);
      const x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t - s * arc;
      let tail = ` scale(${1 + (f.hit ? 0.25 : 0.14) * s})`;
      if (f.hit) tail += ` rotate(${Math.round(360 * t)}deg)`;
      if (f.off && t > 0.7) {
        const q = (t - 0.7) / 0.3;
        tail = ` scale(${1 + (1.09 - 1) * q}, ${1 - 0.69 * q})`;
      }
      frames.push({ transform: `translate(${x}px, ${y}px)${tail}` });
    }
    const a = el.animate(frames, { duration: Math.max(1, f.ms), easing: f.hit ? "ease-in-out" : "cubic-bezier(.2,.7,.3,1)", fill: "forwards" });
    return () => a.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.id]);
  return (
    <g ref={ref} className="nd-fly" pointerEvents="none" style={{ transform: `translate(${f.from[0]}px, ${f.from[1]}px)` }}>
      <NardChecker color={color} />
    </g>
  );
}

/**
 * تخته: بافت‌های تخته‌ی خاتم (public/art/nard از tools/cut_nard_board.py) روی رنگ‌های ساده‌ی
 * پیشین، مهره‌ها، هاله‌ی مبدأها زیرِ مهره‌ها و نشانِ مقصدها **روی** آن‌ها (زدن و ستونِ پر هم
 * دیده شوند). ورودی: لمس، کشیدن (pointer)، و صفحه‌کلید — هر خانه‌ی مجاز یک دکمه است.
 */
function NardBoard({ m, c }: { m: NardLocal; c: BoardOnline }) {
  const sel = useSignal(c.nardFrom);
  const fly = useSignal(c.nardFly);
  const ring = useSignal(c.nardRing);
  const held = useSignal(c.nardHeld);
  const nudge = useSignal(c.nardNudge);
  const svg = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<{ from: number; x: number; y: number; over: number | null } | null>(null);
  const press = useRef<{ id: number; loc: number; x: number; y: number; touch: boolean; started: boolean } | null>(null);
  const swallow = useRef(false);

  const froms = [...new Set(m.moves.map((x) => x.from))];
  const from = drag ? drag.from : sel;
  const dests = (f: number) => [...new Set(m.moves.filter((x) => x.from === f).map((x) => x.to))];
  const tos = from === -99 ? [] : dests(from);
  const color = (p: number) => nardColor(p, m.shift);

  // مهره‌ی در دست یا رهاشده هنوز از مبدأش برداشته است
  const pts = [...m.pts];
  const bar: [number, number] = [m.bar[0], m.bar[1]];
  const away = drag ? { from: drag.from, seat: 0 } : held;
  if (away) {
    const sg = away.seat === 0 ? 1 : -1;
    if (away.from === NARD_BAR) bar[away.seat as 0 | 1] = Math.max(0, bar[away.seat as 0 | 1] - 1);
    else if (away.from >= 0 && pts[away.from]! * sg > 0) pts[away.from] = pts[away.from]! - sg;
  }

  // ---- ورودی ----
  const tap = (i: number) => { if (!swallow.current) c.nardTap(i); };
  const key = (i: number) => (e: RKeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); c.nardTap(i); }
  };
  const toBoard = (e: RPointerEvent): [number, number] | null => {
    const el = svg.current;
    if (!el || typeof el.getScreenCTM !== "function" || typeof el.createSVGPoint !== "function") return null;
    const ctm = el.getScreenCTM();
    if (!ctm) return null;
    const pt = el.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return [p.x, p.y];
  };
  const dropAt = (f: number, x: number, y: number): number | null => {
    const at = nardLocAt(x, y);
    if (at === null) return null;
    const d = dests(f);
    const to = d.includes(at) ? at : nardSnap(at, d);
    return d.includes(to) ? to : null;
  };
  // روی لمسی مهره بالای انگشت کشیده می‌شود تا زیرش گم نشود؛ رها همان‌جا که دیده می‌شود
  const lift = (touch: boolean) => (touch ? NCHK * 0.8 : 0);
  const onDown = (e: RPointerEvent) => {
    if (m.moves.length === 0 || e.button > 0) return;
    const b = toBoard(e);
    if (!b) return;
    const at = nardLocAt(b[0], b[1]);
    if (at === null) return;
    const src = froms.includes(at) ? at : nardSnap(at, froms);
    if (!froms.includes(src)) return;
    press.current = { id: e.pointerId, loc: src, x: b[0], y: b[1], touch: e.pointerType !== "mouse", started: false };
  };
  const onMove = (e: RPointerEvent) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    const b = toBoard(e);
    if (!b) return;
    if (!p.started) {
      if (Math.hypot(b[0] - p.x, b[1] - p.y) < 0.35) return;
      p.started = true;
      try { svg.current?.setPointerCapture(e.pointerId); } catch { /* مرورگرِ بی‌پشتیبان */ }
      c.nardFrom.value = p.loc;
    }
    const y = b[1] - lift(p.touch);
    setDrag({ from: p.loc, x: b[0], y, over: dropAt(p.loc, b[0], y) });
  };
  const onUp = (e: RPointerEvent) => {
    const p = press.current;
    press.current = null;
    if (!p || p.id !== e.pointerId || !p.started) return;
    // کلیکی که پشتِ رهاکردن می‌آید لمس حساب نشود
    swallow.current = true;
    setTimeout(() => { swallow.current = false; }, 0);
    const b = toBoard(e);
    setDrag(null);
    if (b) {
      const y = b[1] - lift(p.touch);
      const to = dropAt(p.loc, b[0], y);
      if (to !== null && c.nardDrop(p.loc, to, [b[0], y])) return;
    }
    c.nudge("فقط مقصدهای سبز");
  };
  const onCancel = () => { press.current = null; setDrag(null); };

  // ---- کشیدن ----
  const under: ReactElement[] = [];   // هاله‌ی ستونِ مبدأها (زیرِ مهره‌ها)
  const checkers: ReactElement[] = [];
  const marks: ReactElement[] = [];   // حلقه‌ی مبدأها و نشانِ مقصدها (روی مهره‌ها)
  const hits: ReactElement[] = [];    // دکمه‌های لمس
  const lit = (i: number) => (from === -99 ? froms.includes(i) : i === from || tos.includes(i) || froms.includes(i));
  const labelOf = (i: number, cnt: number, owner: number) => {
    const what = cnt ? `${fa(cnt)} مهره‌ی ${owner === 0 && !m.watching ? "تو" : c.seatName(owner)}` : "خالی";
    const role = tos.includes(i) ? " — مقصد" : i === from ? " — انتخاب‌شده" : froms.includes(i) ? " — حرکت دارد" : "";
    return `${what}${role}`;
  };
  const srcMark = (i: number, top: [number, number], selected: boolean) => {
    marks.push(<circle key={`sr${i}`} className={selected ? undefined : "nd-pulse"} cx={top[0]} cy={top[1]} r={NDIA * (selected ? 0.58 : 0.53)}
                       fill="none" stroke={selected ? "#ffe05c" : "#ffe680d9"} strokeWidth={selected ? 0.1 : 0.06} />);
  };

  for (let i = 0; i < 24; i++) {
    const { x, top } = nardCol(i);
    const v = pts[i]!, cnt = Math.abs(v), owner = v > 0 ? 0 : 1;
    const isSrc = froms.includes(i);
    if (isSrc) {
      const selected = i === from;
      under.push(<rect key={`h${i}`} className={selected ? undefined : "nd-pulse"} x={x - NPW / 2 + 0.03} y={top ? NTOP : NH / 2 + 0.15}
                       width={NPW - 0.06} height={NH / 2 - NP - 0.15} rx={0.12} fill={selected ? "#ffd84d" : "#ffe673"} opacity={selected ? 0.5 : 0.3} />);
    }
    const lifted = i === from && !drag;
    const dy = lifted ? (top ? 1 : -1) * NCHK * 0.2 : 0;
    const shown = Math.min(cnt, NSTACK);
    for (let k = 0; k < shown; k++) {
      const [cx, cy] = nardChkXY(i, k);
      const up = lifted && k === shown - 1;
      checkers.push(
        <g key={`c${i}_${k}`} transform={`translate(${cx} ${cy + (up ? dy : 0)})${up ? " scale(1.08)" : ""}`}>
          <NardChecker color={color(owner)} />
        </g>,
      );
      if (up || (isSrc && k === shown - 1)) srcMark(i, [cx, cy + (up ? dy : 0)], i === from);
    }
    if (cnt > NSTACK) { const [bx, by] = nardChkXY(i, NSTACK - 1); checkers.push(<NardBadge key={`n${i}`} x={bx} y={by + (lifted ? dy : 0)} n={cnt} />); }
    if (tos.includes(i)) {
      const hit = owner === 1 && cnt === 1;
      const [lx, ly] = hit ? nardChkXY(i, 0) : nardLandXY({ pts, bar, off: m.off }, i, 0);
      const big = drag?.over === i;
      marks.push(
        <g key={`d${i}`} transform={`translate(${lx} ${ly})${big ? " scale(1.25)" : ""}`}>
          <circle r={NDIA * 0.46} fill={NARD_GO} opacity={hit ? 0.45 : 0.6} />
          <circle r={NDIA * (hit ? 0.56 : 0.46)} fill="none" stroke={hit ? NARD_HIT : "#fff"} strokeWidth={hit ? 0.1 : 0.06} />
        </g>,
      );
    }
    const on = lit(i) && m.moves.length > 0;
    hits.push(<rect key={`t${i}`} x={x - NPW / 2} y={top ? NTOP : NH / 2} width={NPW} height={NH / 2 - NP}
                    fill="transparent" className={`bt-col${on ? " on" : ""}`} onClick={() => tap(i)}
                    {...(on ? { role: "button", tabIndex: 0, onKeyDown: key(i), "aria-label": `خانه‌ی ${fa(i + 1)} — ${labelOf(i, cnt, owner)}` }
                            : { "aria-label": `خانه‌ی ${fa(i + 1)}`, "aria-hidden": true })} />);
  }
  // بار: مهره‌های من پایینِ بار، حریف بالا
  for (let p = 0; p < 2; p++) {
    const n = bar[p as 0 | 1];
    const shown = Math.min(n, NBARSTACK);
    for (let k = 0; k < shown; k++) {
      const [bx, by] = nardBarXY(p, k);
      const up = p === 0 && from === NARD_BAR && !drag && k === shown - 1;
      checkers.push(<g key={`b${p}_${k}`} transform={`translate(${bx} ${by})${up ? " scale(1.08)" : ""}`}><NardChecker color={color(p)} /></g>);
      if (p === 0 && froms.includes(NARD_BAR) && k === shown - 1) srcMark(NARD_BAR, [bx, by], from === NARD_BAR);
    }
    if (n > NBARSTACK) { const [bx, by] = nardBarXY(p, NBARSTACK - 1); checkers.push(<NardBadge key={`bn${p}`} x={bx} y={by} n={n} />); }
  }
  if (froms.includes(NARD_BAR)) {
    under.push(<rect key="hbar" className={from === NARD_BAR ? undefined : "nd-pulse"} x={NX1 - 0.06} y={NH / 2 + 0.2} width={NBW + 0.12}
                     height={NH / 2 - NP - 0.2} rx={0.12} fill={from === NARD_BAR ? "#ffd84d" : "#ffe673"} opacity={from === NARD_BAR ? 0.5 : 0.3} />);
  }
  // جمع‌شده‌ها: باریکه‌های روی هم در سینیِ راست — مالِ من پایین، حریف بالا
  for (let p = 0; p < 2; p++) {
    for (let k = 0; k < m.off[p as 0 | 1]; k++) {
      const [ox, oy] = nardOffXY(p, k);
      const ivory = color(p) === NARD_IVORY;
      checkers.push(<rect key={`o${p}_${k}`} x={ox - (NOW - 0.24) / 2} y={oy - 0.15} width={NOW - 0.24} height={0.3} rx={0.08}
                          fill={color(p)} stroke={ivory ? NARD_RIM_IVORY : NARD_RIM_RED} strokeWidth={0.04} />);
    }
  }
  const offDest = tos.includes(NARD_OFF);
  if (offDest) {
    const cy = NH * 0.62, a = NOW * 0.28, cx = NX4 + NOW / 2;
    marks.push(
      <g key="doff" pointerEvents="none" transform={drag?.over === NARD_OFF ? `translate(${cx} ${NH * 0.75}) scale(1.06) translate(${-cx} ${-NH * 0.75})` : undefined}>
        <rect x={NX4} y={NH / 2 + 0.1} width={NOW} height={NH / 2 - NP - 0.1} rx={0.15} fill={NARD_GO} opacity={0.4} stroke={NARD_GO} strokeWidth={0.08} />
        <path d={`M${cx - a},${cy - a / 2} L${cx},${cy + a / 2} L${cx + a},${cy - a / 2}`} fill="none" stroke="#fff" strokeWidth={0.12} strokeLinecap="round" strokeLinejoin="round" />
      </g>,
    );
  }
  const barOn = m.moves.length > 0 && lit(NARD_BAR);
  const offOn = m.moves.length > 0 && offDest;
  hits.push(<rect key="tbar" x={NX1} y={0} width={NBW} height={NH} fill="transparent" className={`bt-col${barOn ? " on" : ""}`}
                  onClick={() => tap(NARD_BAR)}
                  {...(barOn ? { role: "button", tabIndex: 0, onKeyDown: key(NARD_BAR), "aria-label": `بار — ${fa(bar[0])} مهره‌ی تو${from === NARD_BAR ? " — انتخاب‌شده" : " — حرکت دارد"}` }
                             : { "aria-label": "بار", "aria-hidden": true })} />);
  hits.push(<rect key="toff" x={NX4} y={NH / 2} width={NOW} height={NH / 2 - NP} fill="transparent" className={`bt-col${offOn ? " on" : ""}`}
                  onClick={() => tap(NARD_OFF)}
                  {...(offOn ? { role: "button", tabIndex: 0, onKeyDown: key(NARD_OFF), "aria-label": "بیرون بردن — مقصد" }
                             : { "aria-label": "بیرون بردن", "aria-hidden": true })} />);

  return (
    <svg ref={svg} viewBox={`0 0 ${NW} ${NH}`} className="bt-svg nd-board" role="group" aria-label="تخته‌ی تخته‌نرد"
         onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onCancel}>
      <defs>
        <pattern id="ndFelt" patternUnits="userSpaceOnUse" width={3} height={3}>
          <rect width={3} height={3} fill="#1f4a22" />
          <image href={NARD_ART("felt")} width={3} height={3} preserveAspectRatio="none" />
        </pattern>
      </defs>
      <rect x={0} y={0} width={NW} height={NH} rx={0.45} fill="#3b2413" stroke="#c9a05099" strokeWidth={0.06} />
      <rect x={NX0} y={NTOP} width={NX1 - NX0} height={NBOT - NTOP} rx={0.08} fill="url(#ndFelt)" stroke={NARD_BRASS} strokeOpacity={0.75} strokeWidth={0.05} />
      <rect x={NX2} y={NTOP} width={NX3 - NX2} height={NBOT - NTOP} rx={0.08} fill="url(#ndFelt)" stroke={NARD_BRASS} strokeOpacity={0.75} strokeWidth={0.05} />
      <rect x={NX1} y={0.08} width={NBW} height={NH - 0.16} fill="#2c1a0d" />
      <image href={NARD_ART("bar")} x={NX1 + 0.1} y={0.18} width={NBW - 0.2} height={NH - 0.36} preserveAspectRatio="none" />
      <rect x={NX1 + 0.06} y={0.14} width={NBW - 0.12} height={NH - 0.28} rx={0.05} fill="none" stroke={NARD_BRASS} strokeWidth={0.05} />
      <rect x={NX4} y={NTOP} width={NOW} height={NH / 2 - NP - 0.1} rx={0.15} fill="#00000040" />
      <rect x={NX4} y={NH / 2 + 0.1} width={NOW} height={NH / 2 - NP - 0.1} rx={0.15} fill="#00000040" />
      {Array.from({ length: 24 }, (_, i) => {
        const { x, top, c: col } = nardCol(i);
        const light = (col + (top ? 1 : 0)) % 2 === 0;
        const w = NPW - 0.06, base = top ? NTOP : NBOT, tip = top ? NTOP + NTL : NBOT - NTL;
        return (
          <g key={`p${i}`} pointerEvents="none">
            <polygon points={`${x - w / 2},${base} ${x + w / 2},${base} ${x},${tip}`} fill={light ? "#e6d3a3" : "#6a3a1c"} />
            <image href={NARD_ART(light ? "pt_ivory" : "pt_wood")} x={x - w / 2} y={top ? NTOP : NBOT - NTL} width={w} height={NTL}
                   preserveAspectRatio="none" transform={top ? `rotate(180 ${x} ${NTOP + NTL / 2})` : undefined} />
          </g>
        );
      })}
      <g pointerEvents="none">{under}</g>
      <g pointerEvents="none">{checkers}</g>
      <g key={nudge} className={nudge ? "nd-nudge" : undefined} pointerEvents="none">{marks}</g>
      {hits}
      {away && drag && (
        <g transform={`translate(${drag.x} ${drag.y}) scale(1.15)`} pointerEvents="none"><NardChecker color={color(0)} /></g>
      )}
      {away && !drag && held && !fly && (
        <g transform={`translate(${held.at[0]} ${held.at[1]})`} pointerEvents="none"><NardChecker color={color(held.seat)} /></g>
      )}
      {ring && <circle key={ring.id} className="nd-ring" cx={ring.x} cy={ring.y} r={NDIA * 0.5} fill="none" stroke={NARD_HIT} strokeWidth={0.1} pointerEvents="none" />}
      {fly && <NardFly key={fly.id} f={fly} color={color(fly.seat)} />}
    </svg>
  );
}

// ─────────────────────────── مارپله ───────────────────────────

/**
 * میزِ مارپله — همان قابِ منچِ نسخه‌ی ۳ (کاربر، ۲۵ اکتبر ۲۰۲۶: «تغییرهای مشترکِ منچ را برای مارپله هم بیاور»):
 * چهره‌ی گردِ هر بازیکن در یک گوشه با جعبه‌ی تاسش، تاسی که با لمس یا کشیدنِ انگشت روی تخته پرتاب می‌شود، آدمک‌ها
 * به‌جای مهره‌ی گرد، و ایموجی و چت میانِ دو گوشه‌ی پایین؛ واکنش‌ها حبابِ روی چهره‌اند.
 */
function Marpele({ m, c }: { m: MarpeleLocal; c: BoardOnline }) {
  const rolling = useSignal(c.rolling);
  const picked = useSignal(c.mpPicked);
  const thrown = useSignal(c.mpThrow);
  const timer = useSignal(c.timer);
  const reactOn = useActive(useSignal(c.reactUntil));
  const chat = useSeatBubbles(m.n, m.me);
  const col = (s: number) => seatColor(s, m.shift, m.n);
  const sub = (s: number) => (m.pos[s] ?? 0) <= 0 ? "هنوز شروع نشده" : `خانه‌ی ${fa(m.pos[s]!)}`;
  const badges = (s: number) => m.magic ? <PowerBadges p={m.powers[s]} /> : undefined;
  const mine = !m.watching && (m.canRoll || m.choose.length > 0 || !!m.bite);
  const secs = timer && mine ? Math.max(0, Math.ceil(timer.left)) : 0;
  const frac = timer && mine ? Math.max(0, Math.min(1, timer.left / Math.max(0.1, timer.total))) : 1;
  const seatOf = (s: number) => {
    const ss = m.seats[s];
    const me = s === 0 && !m.watching;
    return (
      <RoundSeat name={me ? "تو" : botBare(ss?.name || "حریف", ss?.bot)} avatar={ss?.avatar} country={ss?.country} bot={ss?.bot} me={me} color={col(s)}
                 turn={!m.over && m.turn === s} secs={s === 0 ? secs : 0} frac={frac} sub={sub(s)} extra={badges(s)}
                 cls="mn-seat mp-seat" bubble={chat.get(s)} pid={ss?.playerId} />
    );
  };
  // هر گوشه: چهره و جعبه‌ی تاسش رو به میانه؛ گوشه‌ی بی‌بازیکن خالی (دونفره: روبه‌رو)
  const corner = (k: number) => {
    const s = Array.from({ length: m.n }, (_, i) => i).find((i) => marpeleCorner(i, m.n) === k) ?? -1;
    if (s < 0) return <div className={`mn3-seat c${k} empty`} />;
    return (
      <div className={`mn3-seat c${k}`}>
        {seatOf(s)}
        <MpDiceBox m={m} s={s} rolling={rolling && m.turn === s} picked={picked} onRoll={(v) => c.roll(v)} throwing={thrown?.seat === s} />
      </div>
    );
  };
  const status = m.canRoll ? "نوبت توست؛ روی تاس بزن"
    : m.over ? ""
    : m.bite && !m.watching ? "سر مار هستی — روی تخته انتخاب کن"
    : m.pendingHead ? `${c.seatName(m.turn)} سر مار است — تصمیم می‌گیرد…` : `نوبت ${c.seatName(m.turn)}`;
  return (
    <>
      {/* دو ردیفِ گوشه کمتر از منچ جا می‌گیرند (ساعتِ میانه ندارد)؛ ۲۵۲ ِ منچ تخته را بی‌دلیل کوچک می‌کرد */}
      <Mid ar={10.4 / 11.2} rows={208}
        top={<div className="mn3-row top">{corner(1)}{corner(2)}</div>}
        me={<div className="mn3-row bottom">{corner(0)}<BoardChatButtons onReact={(k) => c.react(k)} />{corner(3)}</div>}>
        <MarpeleBoard m={m} c={c} />
      </Mid>
      <div className="bt-act">
        {!m.watching && m.choose.length > 0 ? <MarpeleChoose m={m} c={c} />
          // تصمیمِ سرِ مار وسطِ خودِ تخته است (BiteCard)؛ این‌جا فقط خطِ وضعیت می‌گوید کجا
          // لحظه‌ی دیگران (نیش، شمشیر، جابه‌جایی): واکنشِ یک‌لمسی جای خطِ وضعیت، مثلِ منچ — نه روی تخته
          : reactOn && !m.watching && !m.canRoll ? <Reactions c={c} cls="mp-react mn-react" />
          : <Status text={status} />}
      </div>
    </>
  );
}

/** پادزهر، شمشیر، «ایست» و ماشین (همیشه با نوبت‌های مانده) ِ یک بازیکن — کنارِ خط ِ پلاک. */
function PowerBadges({ p }: { p?: MarpelePower }) {
  if (!p || (p.antidote === 0 && p.sword === 0 && !p.skip && p.car === 0)) return null;
  const chip = (kind: number, n: number, count = n > 1) => (
    <i className="mp-power" style={{ "--c": MAGIC_LOOK[kind]!.color } as CSSProperties} title={MAGIC_LOOK[kind]!.name}>
      <svg viewBox="-0.3 -0.3 0.6 0.6" width="14" height="14" aria-hidden="true"><MagicArt kind={kind} s={0.5} fill="#fff" /></svg>
      {count && <b>{fa(n)}</b>}
    </i>
  );
  return (
    <span className="mp-powers">
      {p.antidote > 0 && chip(1, p.antidote)}
      {p.sword > 0 && chip(2, p.sword)}
      {p.skip && chip(5, 1)}
      {p.car > 0 && chip(6, p.car, true)}
    </span>
  );
}

/** با تاسِ d مهره‌ی صاحبِ نوبت کجا فرود می‌آید، آخرش کجاست، و از پله (۱) یا مار (−۱) — همان SnakeView.Lead ِ اپ. */
function lead(m: MarpeleLocal, d: number): { land: number; end: number; dir: number } {
  const land = (m.pos[m.turn] ?? 0) + d;
  const j = m.jumps[land];
  const alive = j !== undefined && !m.dead.includes(land);
  return { land, end: alive ? j! : land, dir: alive ? (j! > land ? 1 : -1) : 0 };
}

/** پیکسلِ یک واحدِ پرتاب روی تخته‌ی مارپله: ۱۵ واحدِ منچ = ۱۰ خانه‌ی مارپله، و تخته ۱۰٫۴ خانه پهناست. */
const marpeleUnitPx = () => (document.querySelector(".mp-board")?.getBoundingClientRect().width ?? 0) * (10 / 10.4) / 15;
/** واحدِ پرتاب (۰..۱۵) → درصدِ جا روی صحنه‌ی مارپله (viewBox ِ «-0.2 -0.2 10.4 11.2»). */
const marpelePlace = (x: number, y: number): [string, string] =>
  [`${(((x * 10) / 15 + 0.2) / 10.4 * 100).toFixed(3)}%`, `${(((y * 10) / 15 + 0.2) / 11.2 * 100).toFixed(3)}%`];
/** ضلعِ تاسِ پرتابی: همان ۱٫۴۵ خانه‌ی منچ = ۰٫۹۷ خانه‌ی مارپله. */
const MP_THROWN_DIE = "calc(100cqw * 0.967 / 10.4)";

/**
 * جعبه‌ی تاسِ یک صندلی کنارِ چهره‌اش — همان جعبه‌ی منچ: تاس(های) نوبت در جعبه‌ی صاحبِ نوبت؛ جعبه‌ی خودت دکمه‌ی
 * ریختن است و کشیدنِ انگشت رویش تاس را به همان سو پرتاب می‌کند. با دو تاس، دو تاسِ کوچک کنارِ هم.
 */
function MpDiceBox({ m, s, rolling, picked, onRoll, throwing }:
  { m: MarpeleLocal; s: number; rolling: boolean; picked: number; onRoll: (swipe?: [number, number]) => void; throwing: boolean }) {
  const turn = !m.over && m.turn === s;
  const can = s === 0 && !m.watching && m.canRoll;
  const swipe = useSwipeRoll(can, onRoll, marpeleUnitPx);
  const two = m.diceMode !== DICE_ONE;
  const dice = two ? [m.dice[0] ?? 0, m.dice[1] ?? 0] : [m.die];
  return (
    <button className={`mn-dbox${two ? " two" : ""}${turn ? " turn" : ""}${can ? " on" : ""}`} disabled={!can} {...swipe}
            aria-label={can ? "تاس بریز" : turn ? `تاس نوبت: ${dice.filter((d) => d > 0).map((d) => fa(d)).join(" و ") || "—"}` : "تاس"}>
      {turn && !throwing && dice.map((d, i) => (
        <Die3D key={i} value={d > 0 ? d : 6} rolling={rolling} seed={i + 1} from={[1, -1]} sound={i === 0} skin={m.seats[s]?.dice}
               size={two ? "calc(var(--mn-av, 64px) * .3)" : "calc(var(--mn-av, 64px) * .44)"}
               faded={d === 0 || (!rolling && picked > 0 && d !== picked && dice[0] !== dice[1])} />
      ))}
      {can && <span className="tag">بریز</span>}
    </button>
  );
}

/**
 * دو تاس و انتخاب (فاز ۱): خودِ تاس‌ها دکمه‌اند، جای خطِ وضعیت — هاله به رنگِ جایی که می‌بَرند (سبز پله، قرمز
 * مار، طلایی خانه‌ی ساده) و زیرش «تا …»؛ روی تخته هم خانه‌ی فرودِ هر کدام نشان داده می‌شود (MarpeleChoices).
 */
function MarpeleChoose({ m, c }: { m: MarpeleLocal; c: BoardOnline }) {
  return (
    <div className="mp-choose" role="group" aria-label="کدام تاس؟">
      <span>کدام تاس؟</span>
      {m.dice.slice(0, 2).map((d, i) => {
        if (!m.choose.includes(d)) return null;
        if (i === 1 && m.dice[0] === d) return null;   // جفت: یک انتخاب
        const l = lead(m, d);
        const to = `${l.dir > 0 ? "پله تا " : l.dir < 0 ? "مار تا " : "تا "}${fa(l.end)}`;
        return (
          <button key={i} className={`mp-die-pick${l.dir > 0 ? " up" : l.dir < 0 ? " down" : ""}`}
                  onClick={() => c.chooseDie(d)} aria-label={`تاس ${fa(d)} — ${to}`}>
            <Die3D value={d} rolling={false} size="34px" seed={i + 1} sound={false} skin={m.seats[m.turn]?.dice} />
            <small>{to}</small>
          </button>
        );
      })}
    </div>
  );
}

/**
 * سرِ مار با نیرو در دست: خودت تصمیم می‌گیری — وسطِ خودِ تخته، نه در نوارِ پایین (کاربر، ۲۷ اکتبر ۲۰۲۶: «دکمه‌ی
 * بزرگ وسطِ تخته با نشانشان»). یک ردیف: کاشیِ بزرگِ پادزهر و شمشیر با آرتِ خودِ تخته، رنگِ همان نیرو و شمارِ مانده، و
 * کاشیِ کوچک‌ترِ «بگذار نیش بزند» با بهایش (−۳۷). کارت وسطِ تخته است؛ سرِ مار که زیرش بیفتد، کارت به لبه‌ی دیگرِ تخته
 * می‌چسبد تا حلقه‌ی سرِ مار پیدا بماند. همتای SnakeView.ShowBite ِ اپ.
 */
function BiteCard({ m, c }: { m: MarpeleLocal; c: BoardOnline }) {
  const b = m.bite!;
  const p = m.powers[0];
  const loss = b.head - (m.jumps[b.head] ?? b.head);
  // وسطِ تخته در مختصاتِ آن ۵٫۴ است (viewBox ِ ۱۱٫۲)؛ کارتِ وسط تقریباً ۲٫۶ خانه به هر سو می‌پوشاند
  const [, hy] = marpeleXY(b.head);
  const place = Math.abs(hy - 5.4) < 2.6 ? (hy < 5.4 ? "low" : "high") : "mid";
  const tile = (kind: 1 | 2, left: number) => (
    <button className={`mp-bc-tile k${kind}`} onClick={() => c.biteChoice(kind)}
            aria-label={`${MAGIC_LOOK[kind]!.name}${left > 1 ? ` — ${fa(left)} تا داری` : ""}`}>
      <svg viewBox="-0.5 -0.5 1 1" aria-hidden="true"><MagicArt kind={kind} s={0.92} /></svg>
      <b>{MAGIC_LOOK[kind]!.name}</b>
      {left > 1 && <i className="mp-bc-n" dir="ltr">×{fa(left)}</i>}
    </button>
  );
  return (
    <div className={`mp-bitecard ${place}`} role="group" aria-label="سر مار — انتخاب کن">
      <b className="mp-bc-title">مار! {fa(loss)} خانه پایین می‌بَرد</b>
      <div className="mp-bc-row">
        {b.antidote && tile(1, p?.antidote ?? 1)}
        {b.sword && tile(2, p?.sword ?? 1)}
        <button className="mp-bc-tile let" onClick={() => c.biteChoice(0)} aria-label="بگذار نیش بزند">
          <i className="mp-bc-loss" dir="ltr">−{fa(loss)}</i>
          <b>بگذار نیش بزند</b>
        </button>
      </div>
    </div>
  );
}

/**
 * واکنشِ یک‌لمسی به لحظه‌ی دیگران (مارپله: نیش، شمشیر، جابه‌جایی؛ منچ: زدن) — روی چتِ میز
 * می‌رود. مارپله روی تخته می‌نشیند؛ منچ جای خطِ وضعیت، زیرِ تخته، تا هیچ‌وقت روی خانه‌ها نیفتد.
 */
function Reactions({ c, cls = "mp-react" }: { c: BoardOnline; cls?: string }) {
  const until = useSignal(c.reactUntil);
  const [, force] = useState(0);
  useEffect(() => {
    if (until <= performance.now()) return;
    const t = setTimeout(() => force((x) => x + 1), until - performance.now() + 20);
    return () => clearTimeout(t);
  }, [until]);
  if (until <= performance.now()) return null;
  return (
    <div className={cls} role="group" aria-label="واکنش">
      {reactionPhrases.map((r) => <button key={r.key} onClick={() => c.react(r.key)} aria-label={r.fa}>{r.fa}</button>)}
    </div>
  );
}

function MarpeleBoard({ m, c }: { m: MarpeleLocal; c: BoardOnline }) {
  const fx = useSignal(c.mpFx);
  const walk = useSignal(c.mpWalk);
  const thrown = useSignal(c.mpThrow);
  const ram = useSignal(c.mpRam);
  const strike = useSignal(c.mpStrike);
  const at = new Map<number, number>();
  for (let s = 0; s < m.n; s++) at.set(m.pos[s] ?? 0, (at.get(m.pos[s] ?? 0) ?? 0) + 1);
  const seen = new Map<number, number>();
  const toks: { s: number; y: number; el: ReactElement }[] = [];
  for (let s = 0; s < m.n; s++) {
    const p = m.pos[s] ?? 0;
    // زده‌شده‌ی ماشین در پرواز است (RamFlight)؛ بعد سرِ «شروع» می‌نشیند. شمشیرزن در صحنه‌ی خودش است (MarpeleStrike)
    if (ram?.victims.some((v) => v.s === s) || strike?.seat === s) continue;
    const k = seen.get(p) ?? 0; seen.set(p, k + 1);
    const count = at.get(p) ?? 1;
    let [x, y] = p > 0 ? marpeleXY(p) : [0.6 + s * 0.8, 10.45];
    if (p > 0 && count > 1) { x += Math.cos((k * 2 * Math.PI) / count) * 0.22; y += Math.sin((k * 2 * Math.PI) / count) * 0.12; }
    const shake = !!fx && fx.kind === "bite" && fx.cell === p;
    const w = walk && walk.seat === s ? walk : null;
    const pose: MenchPose = w ? (w.pose === "step" ? (w.step % 2 ? "step1" : "step2") : w.pose === "jump" ? "jump" : w.pose === "fall" ? "fallen" : "scared")
      : m.over && m.winner === s ? "cheer" : "idle";
    // رو به خانه‌ی بعد: ردیف‌ها یکی در میان چپ‌به‌راست و راست‌به‌چپ‌اند
    const face = p > 0 && p < 100 ? (marpeleXY(p + 1)[0] >= marpeleXY(p)[0] ? 1 : -1) : 1;
    toks.push({ s, y, el: <MarpeleToken key={s} pos={p} x={x} y={y} ci={seatArt(s, m.shift, m.n)} girl={m.seats[s]?.piece === 1}
                                         pose={pose} face={face} jumps={m.jumps} shake={shake} fxId={shake ? fx!.id : 0}
                                         car={(m.powers[s]?.car ?? 0) > 0} bump={ram?.seat === s}
                                         knight={(m.powers[s]?.sword ?? 0) > 0} potion={(m.powers[s]?.antidote ?? 0) > 0} /> });
  }
  // پایین‌تر روی پایین‌تر: آدمکِ ردیفِ پایین جلوی آدمکِ ردیفِ بالایی می‌ایستد
  const tokens = toks.sort((a, b) => a.y - b.y || a.s - b.s).map((t) => t.el);
  // سرِ مارهایی که صاحبِ نوبت با تاسِ پیشِ رو ممکن است رویشان بیفتد
  const reach = m.diceMode === DICE_SUM ? 12 : 6;
  const from = m.pos[m.turn] ?? 0;
  const danger = m.over || m.pendingHead ? [] : snakeHeads(m.jumps).filter((h) => h > from && h <= from + reach && !m.dead.includes(h));
  return (
    <>
    <svg viewBox="-0.2 -0.2 10.4 11.2" className="bt-svg mp-board" role="img" aria-label="تخته‌ی مارپله">
      <MarpeleBoardArt jumps={m.jumps} theme={m.theme} dead={m.dead} />
      <MarpeleDanger heads={danger} />
      {m.magic && <MarpeleMagics magics={m.magics} />}
      {m.choose.length > 0 && <MarpeleChoices m={m} />}
      {m.bite && !m.watching && (
        <g transform={`translate(${marpeleXY(m.bite.head).join(" ")})`} pointerEvents="none">
          <circle className="mp-danger" r={0.5} fill="none" stroke="#e2503a" strokeWidth={0.08} />
        </g>
      )}
      <text x={9.8} y={10.55} textAnchor="end" fontSize={0.3} fill="#ffffff90">شروع</text>
      <text x={-0.1} y={10.55} fontSize={0.26} fill="#ffffff70">{themeOf(m.theme).name}</text>
      {tokens}
      {ram && ram.victims.map((v) => (
        <RamFlight key={`${ram.id}-${v.s}`} from={v.from} to={v.to} ci={seatArt(v.s, m.shift, m.n)} girl={m.seats[v.s]?.piece === 1}
                   knight={(m.powers[v.s]?.sword ?? 0) > 0} />
      ))}
      {strike && <MarpeleStrike key={strike.id} head={strike.head} ms={strike.ms} ci={seatArt(strike.seat, m.shift, m.n)}
                                girl={m.seats[strike.seat]?.piece === 1} potion={(m.powers[strike.seat]?.antidote ?? 0) > 0} />}
      {fx && <MarpeleFx key={fx.id} fx={fx} jumps={m.jumps} />}
    </svg>
    {m.bite && !m.watching && <BiteCard m={m} c={c} />}
    {thrown && thrown.throws.map((t, i) => (
      <ThrownDie key={`${thrown.id}-${i}`} th={{ corner: thrown.corner, v: t.v, value: t.value, ms: thrown.ms, hold: thrown.hold, id: thrown.id, dx: t.dx }}
                 place={marpelePlace} size={MP_THROWN_DIE} sound={i === 0} skin={m.seats[thrown.seat]?.dice} />
    ))}
    </>
  );
}

/** دو تاس: خانه‌ی فرودِ هر تاس روی تخته، با عددش — انتخاب از روی خودِ تاس است، این فقط نشان می‌دهد کجا. */
function MarpeleChoices({ m }: { m: MarpeleLocal }) {
  return (
    <g pointerEvents="none">
      {m.choose.map((d) => {
        const { land, dir } = lead(m, d);
        const [x, y] = marpeleXY(land);
        const col = dir > 0 ? "#7be08c" : dir < 0 ? "#e2503a" : "#ffffff";
        const ly = y > 1 ? -0.95 : 0.55;   // ردیفِ بالا: برچسب زیرِ خانه
        return (
          <g key={d} className="mp-hint" transform={`translate(${x} ${y})`}>
            <circle className="mp-danger" r={0.45} fill="none" stroke={col} strokeWidth={0.07} />
            <rect x={-0.25} y={ly} width={0.5} height={0.4} rx={0.12} fill="#14122be0" />
            <text y={ly + 0.29} textAnchor="middle" fontSize={0.3} fontWeight={700} fill="#fff">{fa(d)}</text>
          </g>
        );
      })}
    </g>
  );
}

/** جلوه‌ی یک لحظه روی خانه: نیش (انفجارِ قرمز)، شمشیر (برق)، پادزهر (حلقه‌ی سبز)، برداشتنِ جادو. */
function MarpeleFx({ fx, jumps }: { fx: { kind: string; cell: number; magic?: number }; jumps: Jumps }) {
  const [x, y] = marpeleXY(fx.cell);
  // نیش: سرِ نیش‌زننده‌ی همان مار (آرتِ کاربر) روی سرِ آرام می‌نشیند، جلو می‌پرد و برمی‌گردد
  const bite = fx.kind === "bite" ? snakeBite(jumps, fx.cell) : null;
  const color = fx.kind === "bite" ? "#e2503a" : fx.kind === "antidote" ? "#34b86a" : fx.kind === "sword" ? "#ffffff"
    : MAGIC_LOOK[fx.magic ?? 0]?.color ?? "#fff";
  return (
    <g className={`mp-fx mp-fx-${fx.kind}`} transform={`translate(${x} ${y})`} pointerEvents="none">
      {fx.kind === "sword"
        ? <path d="M-0.6,0.5 L0.6,-0.5" stroke={color} strokeWidth={0.1} strokeLinecap="round" pathLength={1} />
        : <circle r={0.5} fill="none" stroke={color} strokeWidth={0.1} />}
      {fx.kind === "bite" && <circle r={0.28} fill={color} opacity={0.5} />}
      {/* مارِ دهان‌باز روی همان مار (همان تبدیل، تنه همان‌جا) — جلو می‌جهد و برمی‌گردد، لولا مرکزِ سر */}
      {bite && (
        <g transform={`translate(${-x} ${-y})`}>
          <g className="mp-bite-head" style={biteOrigin(bite.fit)}><SnakeImage fit={bite.fit} src={bite.src} /></g>
        </g>
      )}
    </g>
  );
}

/** آدمکِ مارپله: بلندی ۱٫۴ خانه (کمی بزرگ‌تر از نسبتِ منچ تا روی خانه‌های شلوغِ مار و پله گم نشود)، کفِ پا ۰٫۳ خانه پایینِ مرکزِ خانه. */
const MP_PIECE_H = 1.4, MP_FEET = 0.3;

/**
 * آدمکِ مارپله — همان آدمک‌های منچ (رنگِ صندلی، پسر یا دخترِ انتخابِ بازیکن). گام‌ها با گذارِ کوتاهِ CSS خانه‌به‌خانه
 * می‌روند و پا عوض می‌شود (mpWalk)؛ پرش روی مار یا نردبان راهِ خودش را می‌رود — روی بدنِ مار پایین سُر می‌خورد
 * (ترسیده، بعد افتاده)، از نردبان بالا می‌پرد — نه خطِ راست. نیش که بخورد، پیش از سُر خوردن می‌لرزد.
 */
function MarpeleToken({ pos, x, y, ci, girl, pose, face, jumps, shake, fxId, car, bump, knight, potion }: {
  pos: number; x: number; y: number; ci: number; girl: boolean; pose: MenchPose; face: number; jumps: Jumps; shake: boolean; fxId: number;
  car: boolean; bump: boolean; knight: boolean; potion: boolean;
}) {
  const ref = useRef<SVGGElement>(null);
  // زره تا همه‌ی ژست‌هایش بار نشده، آدمکِ ساده — وگرنه SVG تا رسیدنِ تصویر هیچ نمی‌کشید
  const armored = useArtReady(knight ? knightSet(ci, girl) : NO_ART) && knight;
  const prev = useRef(pos);
  useEffect(() => {
    const from = prev.current;
    prev.current = pos;
    const g = ref.current;
    if (!g || typeof g.animate !== "function" || from <= 0 || jumps[from] !== pos) return;
    const pts = jumpPath(jumps, from, pos);
    pts[pts.length - 1] = [x, y];
    let d = 0;
    for (let i = 1; i < pts.length; i++) d += Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
    const snake = pos < from;
    const dur = ms(snake ? Math.min(1000, Math.max(420, 320 + 95 * d)) : Math.min(800, Math.max(380, 300 + 70 * d)));
    const a = g.animate(pts.map(([px, py]) => ({ transform: `translate(${px}px, ${py}px)` })),
      { duration: dur, easing: snake ? "ease-in" : "ease-in-out" });
    return () => a.cancel();
    // فقط با عوض‌شدنِ خانه؛ جابه‌جاییِ کوچکِ هم‌خانه‌ها پرش نیست
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);
  return (
    <g ref={ref} className={`bt-tok mp-tok${pos === 0 ? " home" : ""}${car ? " car" : ""}`} style={{ transform: `translate(${x}px, ${y}px)` }}
       data-pose={car ? (bump ? "bump" : "car") : pose} data-knight={knight || undefined} data-potion={potion || undefined}>
      <g key={fxId} className={shake ? "mp-shake" : undefined}>
        <ellipse cx={0} cy={MP_FEET} rx={car ? 0.62 : 0.3} ry={0.09} fill="#000" opacity={0.32} />
        <g transform={`translate(0 ${MP_FEET})${face < 0 ? " scale(-1 1)" : ""}`}>
          {car ? <CarArt ci={ci} girl={girl} bump={bump} />
            : <>
                {armored ? <KnightArt ci={ci} girl={girl} pose={pose} /> : <PieceArt ci={ci} pose={pose} girl={girl} h={MP_PIECE_H} />}
                {potion && <PiecePotion at={POTION_AT[armored ? (girl ? "kg" : "kp") : (girl ? "g" : "p")][KNIGHT_POSES.indexOf(pose)]!} />}
              </>}
        </g>
      </g>
    </g>
  );
}

/** زده‌شده‌ی ماشین: افتاده و چرخان، روی کمانی بلند از خانه‌اش تا جایش روی نوارِ «شروع» — همان FlyHome ِ اپ. */
function RamFlight({ from, to, ci, girl, knight }: { from: [number, number]; to: [number, number]; ci: number; girl: boolean; knight: boolean }) {
  const ref = useRef<SVGGElement>(null);
  useEffect(() => {
    const g = ref.current;
    if (!g || typeof g.animate !== "function") return;
    const frames = Array.from({ length: 13 }, (_, i) => {
      const t = i / 12, x = from[0] + (to[0] - from[0]) * t, y = from[1] + (to[1] - from[1]) * t - Math.sin(t * Math.PI) * 2.2;
      return { transform: `translate(${x}px, ${y}px) rotate(${t * 540}deg)` };
    });
    const a = g.animate(frames, { duration: ms(640), easing: "ease-in-out", fill: "forwards" });
    return () => a.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <g ref={ref} className="mp-rammed" style={{ transform: `translate(${from[0]}px, ${from[1]}px)` }} pointerEvents="none">
      <g transform={`translate(0 ${MP_FEET})`}>
        {knight && artReady.has(knightSrc(ci, girl, "fallen")) ? <KnightArt ci={ci} girl={girl} pose="fallen" />
          : <PieceArt ci={ci} pose="fallen" girl={girl} h={MP_PIECE_H} />}
      </g>
    </g>
  );
}

// ─────────────────────────── شمشیر و پادزهرِ در دست ───────────────────────────
// کاربر (۲۶ اکتبر ۲۰۲۶): «کسی که شمشیر یا دارو دارد شکلِ دیگری داشته باشد — مثلاً زره؛ و وقتی شمشیر را به کار می‌برد،
// انیمیشنی باشد که با شمشیر به مار می‌زند». آرت از tools/marpele_knight_art.py (پرامپتِ آرتِ کشیده‌ی کاربر:
// art/prompts/marpele_knight)؛ جای آویزِ شیشه و نوکِ شمشیر در marpeleKnightData.ts. همتای SnakeView ِ اپ.

type KnightFrame = MenchPose | "strike1" | "strike2" | "strike3";
/** آرتِ آدمکِ زره‌پوش: ژست‌ها همان کادرِ ۱۸۰×۲۴۰ ِ آدمکِ منچ، قاب‌های ضربه ۳۶۰×۳۶۰ ِ صحنه‌ی چوبدستی. */
const knightSrc = (ci: number, girl: boolean, f: KnightFrame) => `/art/marpele/knight-${girl ? "g" : "p"}${ci}-${f}.webp`;
const KNIGHT_FRAMES: readonly KnightFrame[] = [...KNIGHT_POSES, "strike1", "strike2", "strike3"];
const knightSet = (ci: number, girl: boolean): string[] => KNIGHT_FRAMES.map((f) => knightSrc(ci, girl, f));
const NO_ART: string[] = [];

/** تصویرهایی که مرورگر بارشان کرده (برای «تا آرت نرسیده، همان قبلی»). */
const artReady = new Set<string>();
function loadArt(src: string): Promise<void> {
  return new Promise<void>((done) => {
    const img = new Image();
    img.onload = () => { artReady.add(src); done(); };
    img.onerror = () => done();
    img.src = src;   // تکرارِ یک نشانی از حافظه‌ی مرورگر می‌آید
  });
}
/** همه‌ی srcs بار شده‌اند؟ اگر نه بارشان می‌کند و بعد دوباره می‌کشد. */
function useArtReady(srcs: readonly string[]): boolean {
  const ready = srcs.every((s) => artReady.has(s));
  const [, force] = useState(0);
  const key = srcs.join("|");
  useEffect(() => {
    if (ready || typeof Image === "undefined") return;
    let live = true;
    void Promise.all(srcs.filter((s) => !artReady.has(s)).map(loadArt)).then(() => { if (live) force((x) => x + 1); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);
  return ready;
}

/** آدمکِ زره‌پوش با کفِ پا روی (۰، ۰) — همان قاب و اندازه‌ی PieceArt ِ مارپله. */
function KnightArt({ ci, girl, pose }: { ci: number; girl: boolean; pose: MenchPose }) {
  const h = MP_PIECE_H, w = h * 0.75;
  return <image href={knightSrc(ci, girl, pose)} x={-w / 2} y={-h * (1 - FEET_IN_ART)} width={w} height={h} preserveAspectRatio="none" />;
}

/** بلندیِ شیشه‌ی پادزهرِ آویز (خانه) — یک‌پنجمِ آدمک، تا روی گوشی هم دیده شود. */
const POTION_H = 0.46;
/**
 * شیشه‌ی پادزهرِ تخته (آرتِ potion) آویز از کمربند: at = [x، y، زاویه] به پیکسلِ کادرِ آرت (marpeleKnightData)، frame
 * کادرِ همان آرت؛ کمی تاب می‌خورد. آدمکِ رو به چپ با گروهِ خودش آینه می‌شود.
 */
function PiecePotion({ at, frame = 240 }: { at: readonly [number, number, number]; frame?: 240 | 360 }) {
  const h = frame === 240 ? MP_PIECE_H : STRIKE_ART, w = frame === 240 ? h * 0.75 : h;
  const x = -w / 2 + (at[0] / (frame === 240 ? 180 : 360)) * w;
  const y = -h * (1 - 10 / frame) + (at[1] / frame) * h;
  return (
    <g transform={`translate(${x.toFixed(3)} ${y.toFixed(3)}) rotate(${at[2]})`} pointerEvents="none">
      <g className="mp-potion"><IconArt name="potion" x={0} y={POTION_H / 2 - 0.04} s={POTION_H} /></g>
    </g>
  );
}

/** قابِ صحنه‌ی شمشیر: ۳۶۰×۳۶۰ با همان مقیاسِ پیکسلِ آدمک (۲٫۱ خانه)، کفِ پا ۱۰px بالای لبه‌ی پایین و وسطِ افقی. */
const STRIKE_ART = (MP_PIECE_H * 360) / 240;
const strikeXY = ([px, py]: readonly [number, number]): [number, number] => [((px - 180) / 360) * STRIKE_ART, ((py - 350) / 360) * STRIKE_ART];
function StrikeArt({ ci, girl, f }: { ci: number; girl: boolean; f: 1 | 2 | 3 }) {
  return <image href={knightSrc(ci, girl, `strike${f}`)} x={-STRIKE_ART / 2} y={-STRIKE_ART * (1 - 10 / 360)}
                width={STRIKE_ART} height={STRIKE_ART} preserveAspectRatio="none" />;
}

/**
 * صحنه‌ی شمشیر: آدمکِ زره‌پوش از وسطِ خانه کنارِ سرِ مار می‌رود و سه قابِ آرت پشتِ هم می‌آیند — شمشیر کنارِ کمر، بالای سر،
 * فرود بر سرِ مار — با خم و کشِ تن میانِ قاب‌ها، ردِ تیغه از نوکِ قابِ ۲ تا نوکِ قابِ ۳، و برق و جرقه درست سرِ نوکِ تیغه، که
 * روی سرِ مار است (STRIKE_TIP). کنارِ ستونِ اول رو به چپ می‌زند. بعد شمشیر کنارِ کمر برمی‌گردد سرِ خانه. زمان‌بندی از --st
 * (keyframes ِ st* در style.css)، ضربه در STRIKE_IMPACT؛ کنترلر همان لحظه مار را بی‌جان می‌کند.
 */
function MarpeleStrike({ head, ms: dur, ci, girl, potion }: { head: number; ms: number; ci: number; girl: boolean; potion: boolean }) {
  const [hx, hy] = marpeleXY(head);
  const tips = STRIKE_TIP[girl ? "g" : "p"].map(strikeXY);
  const [tx, ty] = tips[2]!;
  const dir = hx - tx < 0.3 ? -1 : 1;
  // کمی بالاتر از ایستادنِ همیشگی (کفِ پا ۰٫۳ زیرِ مرکز)، تا نوکِ تیغه وسطِ سرِ مار بخورد نه زیرِ آن
  const fx = hx - dir * tx, fy = hy + MP_FEET - 0.14;
  const [ax, ay] = tips[1]!;
  const r = Math.hypot(tx - ax, ty - ay) * 0.72;
  return (
    <g className="mp-strike" pointerEvents="none" transform={`translate(${fx.toFixed(3)} ${fy.toFixed(3)})`}
       style={{ "--st": `${dur}ms`, "--sd": `${(hx - fx).toFixed(3)}px` } as CSSProperties} data-head={head}>
      <g className="st-move">
        <ellipse rx={0.32} ry={0.09} fill="#000" opacity={0.3} />
        <g transform={dir < 0 ? "scale(-1 1)" : undefined}>
          <g className="st-body">
            {([1, 2, 3] as const).map((f) => (
              <g key={f} className={`st-f${f}`}>
                <StrikeArt ci={ci} girl={girl} f={f} />
                {potion && <PiecePotion at={POTION_AT[girl ? "sg" : "sp"][f - 1]!} frame={360} />}
              </g>
            ))}
          </g>
          <path className="st-swoosh" d={`M ${ax.toFixed(3)} ${ay.toFixed(3)} A ${r.toFixed(3)} ${r.toFixed(3)} 0 0 1 ${tx.toFixed(3)} ${ty.toFixed(3)}`}
                pathLength={1} fill="none" stroke="#f4fbff" strokeWidth={0.11} strokeLinecap="round" />
        </g>
      </g>
      <g transform={`translate(${(dir * tx).toFixed(3)} ${ty.toFixed(3)})`}>
        <g className="st-pow"><polygon points={starPoints(0, 0, 0.4, 8, 0.45)} fill="#ffffff" stroke="#bcd6ff" strokeWidth={0.04} strokeLinejoin="round" /></g>
        {Array.from({ length: 7 }, (_, i) => {
          const a = -Math.PI / 2 + (i - 3) * 0.42;
          return <circle key={i} className="st-spark" r={0.05} fill="#ffe27a"
                         style={{ "--sx": `${(Math.cos(a) * 0.62 * dir).toFixed(3)}px`, "--sy": `${(Math.sin(a) * 0.55).toFixed(3)}px` } as CSSProperties} />;
        })}
      </g>
    </g>
  );
}

// ─────────────────────────── پایان ───────────────────────────

function GameOver({ m, c }: { m: BoardLocal; c: BoardOnline }) {
  if (m.game === "mench" && m.v2) return <MenchOver m={m} c={c} />;
  const draw = m.game === "dooz" && m.draw;
  const won = !m.watching && m.winner === 0;
  const who = m.winner >= 0 ? c.seatName(m.winner) : "";
  const kind = m.game === "nard" ? (m.points === 3 ? " — جول!" : m.points === 2 ? " — مارس!" : "") : "";
  const title = draw ? "مساوی!" : won
    ? ({ mench: "همه‌ی مهره‌ها رسیدند — بردی!", dooz: "چهارتا پشت هم — بردی!", nard: "بردی", domino: "به ۱۰۰ رسیدی — بردی!", marpele: "به صد رسیدی — بردی!" }[m.game] + kind)
    : `${who} برد${kind}`;
  const sub = draw ? "تخته پر شد و کسی چهارتا نچید."
    : m.game === "mench" ? (won ? "چهار مهره‌ات را زودتر از همه به مرکز رساندی." : `چهار مهره‌ی ${who} زودتر رسید.`)
    : m.game === "dooz" ? (won ? "چهارتای تو زودتر کامل شد." : `چهارتای ${who} زودتر کامل شد.`)
    : m.game === "nard" ? (won ? "همه‌ی پانزده مهره را زودتر بیرون بردی." : `مهره‌های ${who} زودتر بیرون رفتند.`)
    : m.game === "domino" ? `${m.seats.map((_, s) => `${c.seatName(s)} ${fa(m.score[s] ?? 0)}`).join(" · ")}`
    : (won ? "پله‌ها با تو بودند." : `مهره‌ی ${who} زودتر به صد رسید.`);
  return (
    <Overlay label="پایان بازی" card={draw || m.watching ? "even" : won ? "win" : "lose"}
             after={<ResultAd won={draw || m.watching ? null : won} watching={m.watching} />}>
      <div className="vt">{title}</div>
      <div className="muted">{sub}</div>
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {m.watching ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}
